<template>
  <div class="add-survey">
    <transition name="fade">
      <div v-if="!loading"
           class="add-survey-wrap">
        <div class="add-header"
             :class="{ scroll: scrollTop > 0, clear: !requireNoRunning, editing: isEditing && !isDraft }"
             :style="{ height: `${231 - scrollTop}px` }">
          <ui-button :iconName="'icon--arrow-left-black'"
                     @click="toDashboard">&nbsp;&nbsp;{{ $t('userResearch.survey.goBackList') }}&nbsp;&nbsp;</ui-button>
          <div v-if="!(isEditing && !isDraft)"
               class="indicator-gap"
               :key="`gap${scrollTop}`"
               :style="{ height: `${Math.max(63 - scrollTop / (requireNoRunning ? 1.5 : 2), 0)}px` }">
          </div>
          <div v-if="!(isEditing && !isDraft)"
               class="step-indicator">
            <div class="step step-1"
                 :class="{ ing: step === 1, opened: stepCompleted.step1 === true, complete: isStep1Complete }"
                 @click="goStep(1, true)">
              <span class="bullet">
                <i class="icon icon--check-white-size14"></i>
                <i class="number">1</i>
              </span>
              <p>{{ $t('userResearch.survey.create.steps.step1') }}</p>
            </div>
            <div class="step step-2"
                 :class="{ ing: step === 2, opened: stepCompleted.step2 === true, complete: (step === 2 || stepCompleted.step2 === true) && isStep2Complete }"
                 @click="goStep(2, true)">
              <span class="bullet">
                <i class="icon icon--check-white-size14"></i>
                <i class="number">2</i>
              </span>
              <p>{{ $t('userResearch.survey.create.steps.step2') }}</p>
            </div>
            <div class="step step-3"
                 :class="{ ing: step === 3, opened: stepCompleted.step3 === true, complete: (step === 3 || stepCompleted.step3 === true) && isStep3Complete }"
                 @click="goStep(3, true)">
              <span class="bullet">
                <i class="icon icon--check-white-size14"></i>
                <i class="number">3</i>
              </span>
              <p>{{ $t('userResearch.survey.create.steps.step3') }}</p>
            </div>
            <div class="step step-4"
                 :class="{ ing: step === 4, opened: stepCompleted.step4 === true, complete: (step === 4 || stepCompleted.step4 === true) && isStep4Complete }"
                 @click="goStep(4, true)">
              <span class="bullet">
                <i class="icon icon--check-white-size14"></i>
                <i class="number">4</i>
              </span>
              <p>{{ $t('userResearch.survey.create.steps.step4') }}</p>
            </div>
          </div>

          <div v-if="requireNoRunning"
               class="add-notice">
            <div class="add-notice-wrap">
              <i class="icon icon--ring"></i>
              {{ $t('userResearch.survey.create.hasRunning') }}
            </div>
          </div>
        </div>
        <div class="add-wrap"
             :class="{ clear: !requireNoRunning, editing: isEditing && !isDraft }"
             :style="{ height: `calc(100vh - ${309 - scrollTop}px)` }"
             @scroll="onScroll">
          <div class="add-body">
            <div class="step step-1"
                 :class="{ ing: step === 1, opened: stepCompleted.step1 === true, editing: isEditing && !isDraft && isStep1Complete, complete: stepCompleted.step1 === true && isStep1Complete && !(isEditing && !isDraft), animating: animating === true }"
                 :style="{ 'max-height': maxStep1Height + 'px'  }">
              <div class="step-header"
                   @click="goStep(1, true)">
                <span class="bullet">
                  <i class="icon icon--check-white-size16"></i>
                  <i class="number">1</i>
                </span>
                <h4>{{ $t('userResearch.survey.create.stepTitle.step1') }}</h4>
                <span v-if="isEditing && !isDraft"
                      class="arrow"
                      :class="{ disabled: !isStep1Complete }"></span>
                <button v-else
                        :disabled="!isStep1Complete"
                        class="next"
                        @click="goStep(2)">
                  {{ $t('common.next') }}
                </button>
              </div>
              <div class="step-body">
                <div class="step-item">
                  <ui-input-text :type="'text'"
                                 :placeholder="$t('userResearch.survey.create.placeholder.surveyName')"
                                 :modelValue="model.surveyName"
                                 :error="model.surveyName ? (model.surveyName.length > 1000 ? $t('common.validationErrorText.lessCharacter', { length: '1,000' }) : '') :
                                    $t('userResearch.survey.create.error.surveyName')"
                                 @input="onInput('surveyName', $event)">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.surveyName') }}
                      <i class="icon icon--required"></i>
                      <span class="description">
                      {{ $t('userResearch.survey.create.adminOnly') }}
                      </span>
                    </h5>
                  </ui-input-text>
                </div>

                <div class="step-item">
                  <ui-input-text :type="'textarea'"
                                 :placeholder="$t('userResearch.survey.create.placeholder.surveyIntro')"
                                 :modelValue="model.surveyIntro"
                                 :error="model.surveyIntro.length > 1000 ? $t('common.validationErrorText.lessCharacter', { length: '1,000' }) : ''"
                                 @input="onInput('surveyIntro', $event)"
                                 @onFocus="modelError.surveyIntro = ''">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.intro') }}
                      <ui-icon-tooltip :iconSize="'outline-white'"
                                       arrowBottom
                                       arrowCenter>
                        <span v-html="$t('userResearch.survey.create.introDescription')"></span>
                      </ui-icon-tooltip>
                    </h5>
                  </ui-input-text>
                </div>

                <div class="step-item privacy">
                  <div class="title">
                    <span class="ba-switch-wrap" @click="changeIsSurveyPrivacy">
                      <ba-switch :value="isSurveyPrivacy"
                                 :default="false"
                                 :onColor="'#3eaa08'"
                                 :offColor="'#d7d7d7'"
                                 :onLabel="$t('common.on')"
                                 :offLabel="$t('common.off')"
                                 :width="44"
                                 :size="'small'"

                      />
                    </span>
                    <span class="label">
                      {{ $t('userResearch.survey.create.privacy.title') }}
                    </span>
                  </div>
                  <div class="desc">
                    <span class="txt">{{ $t('userResearch.survey.create.privacy.desc') }}</span>
                    <button class="example" v-if="isSurveyPrivacy" @click="openPrivacyExample">
                      <span>
                        {{ $t('userResearch.survey.create.privacy.example') }}
                      </span>
                      <i class="icon icon--arrow-right-gray-xsmall"></i>
                    </button>
                  </div>
                  <transition name="fade">
                    <div class="content" v-if="isSurveyPrivacy">
                      <ui-input-text :type="'textarea'"
                                     :placeholder="$t('userResearch.survey.create.privacy.placeholder')"
                                     :modelValue="model.privacyAgrmt"
                                     :error="isSurveyPrivacy ? (model.privacyAgrmt ? (model.privacyAgrmt.length > 1000 ? $t('common.validationErrorText.lessCharacter', { length: '1,000' }) : '') : $t('userResearch.survey.create.privacy.error.privacyDesc')) : ''"
                                     @input="onInput('privacyAgrmt', $event)">
                      </ui-input-text>
                    </div>
                  </transition>
                </div>

                <div class="step-item questions">
                  <h5 class="item-title">
                    {{ $t('userResearch.survey.create.question') }}
                    <i class="icon icon--required"></i>
                  </h5>
                  <div class="question-items">
                    <draggable v-model="model.questions"
                               v-bind="dragOptions"
                               draggable=".question"
                               handle=".handle"
                               @start="dragging = true"
                               @end="dragging = false">
                      <transition-group type="transition">
                        <template v-for="(question, index) in computedQuestions">
                          <div :key="`question-${index}`"
                               class="question">
                            <div class="handle"></div>
                            <div class="buttons">
                              <button @click="duplicateQuestion(question.index)"
                                      :disabled="computedQuestions.length > 99">
                                <i class="icon icon--item-duplicate"></i>
                              </button>
                              <span class="gap"></span>
                              <button :disabled="computedQuestions.length <= 1"
                                      @click="deleteQuestion(question.index)">
                                <i class="icon icon--item-delete"></i>
                              </button>
                              <button class="required"
                                      @click="onInputQuestion(question.index, 'require', !question.require)">
                                {{ $t('common.required') }}
                                <ui-checkbox :value="question.require" />
                              </button>
                            </div>
                            <div class="head">
                              <div class="index">
                                No. {{ index + 1 }}
                              </div>
                              <div class="type">
                                <ui-selectbox :width="220"
                                              :value="question.questionType"
                                              :items="questionTypes"
                                              :disabled="isEditing && question.questionId !== null && !isDraft"
                                              @mouseenter="onShowQuestionType(question.index, $event)"
                                              @mouseleave="onHideQuestionType(question.index)"
                                              @input="onChangeQuestionType(question.index, $event)" />
                                <ui-tooltip v-if="isEditing && question.questionId !== null && !isDraft"
                                            arrowCenter arrowBottom>
                                  {{ $t('userResearch.survey.create.noChange.questionType') }}
                                </ui-tooltip>
                                <transition name="fade">
                                  <div v-if="showQuestionType[question.index] === 'multiple'"
                                       class="type-tooltip multiple">
                                    <div class="type-name">
                                      {{ $t('userResearch.survey.create.typeGuide.multiple.title') }}
                                    </div>
                                    <div class="type-description">
                                      {{ $t('userResearch.survey.create.typeGuide.multiple.description') }}
                                    </div>
                                    <div class="type-example">
                                      <div class="exp-title">
                                        {{ $t('userResearch.survey.create.typeGuide.multiple.example1') }}
                                      </div>
                                      <p>
                                        {{ $t('userResearch.survey.create.typeGuide.multiple.example2') }}
                                      </p>
                                      <ul>
                                        <li>
                                          <span class="radio"></span>
                                          {{ $t('userResearch.survey.create.typeGuide.multiple.example3') }}
                                        </li>
                                        <li>
                                          <span class="radio"></span>
                                          {{ $t('userResearch.survey.create.typeGuide.multiple.example4') }}
                                        </li>
                                      </ul>

                                      <div class="exp-title">
                                        {{ $t('userResearch.survey.create.typeGuide.multiple.example5') }}
                                      </div>
                                      <p>
                                        {{ $t('userResearch.survey.create.typeGuide.multiple.example6') }}
                                      </p>
                                      <ul>
                                        <li>
                                          <span class="check"></span>
                                          {{ $t('userResearch.survey.create.typeGuide.multiple.example7') }}
                                        </li>
                                        <li>
                                          <span class="check"></span>
                                          {{ $t('userResearch.survey.create.typeGuide.multiple.example8') }}
                                        </li>
                                        <li>
                                          <span class="check"></span>
                                          {{ $t('userResearch.survey.create.typeGuide.multiple.example9') }}
                                        </li>
                                        <li>
                                          <span class="check"></span> {{ $t('userResearch.survey.create.typeGuide.multiple.example10') }} : <span class="input-text"></span>
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  <div v-else-if="showQuestionType[question.index] === 'essay'"
                                       class="type-tooltip essay">
                                    <div class="type-name">
                                      <div class="type-name">
                                        {{ $t('userResearch.survey.create.typeGuide.essay.title') }}
                                      </div>
                                      <div class="type-description">
                                        {{ $t('userResearch.survey.create.typeGuide.essay.description') }}
                                      </div>
                                    </div>
                                    <div class="type-example">
                                      <p>
                                        {{ $t('userResearch.survey.create.typeGuide.essay.example1') }}
                                      </p>
                                      <div class="input-textarea"><span></span></div>
                                    </div>
                                  </div>
                                  <div v-else-if="showQuestionType[question.index] === 'preference'"
                                       class="type-tooltip preference">
                                    <div class="type-name">
                                      {{ $t('userResearch.survey.create.typeGuide.preference.title') }}
                                    </div>
                                    <div class="type-description">
                                      {{ $t('userResearch.survey.create.typeGuide.preference.description') }}
                                    </div>
                                    <div class="type-example">
                                      <p>
                                        {{ $t('userResearch.survey.create.typeGuide.preference.example1') }}
                                      </p>
                                      <div class="input-range">
                                        <ul>
                                          <li><em>1</em><span></span></li>
                                          <li><em>2</em><span></span></li>
                                          <li><em>3</em><span></span></li>
                                          <li><em>4</em><span></span></li>
                                          <li><em>5</em><span></span></li>
                                        </ul>
                                        <div class="range-text">
                                          <div class="left">
                                            {{ $t('userResearch.survey.create.typeGuide.preference.example2') }}
                                          </div>
                                          <div class="right">
                                            {{ $t('userResearch.survey.create.typeGuide.preference.example3') }}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </transition>
                              </div>
                              <div class="title">
                                <ui-input-text :type="'text'"
                                               :id="`question-${index}`"
                                               :placeholder="$t('userResearch.survey.create.placeholder.questionTitle.' + question.questionType + (question.allowMultiple ? 'Many' : ''))"
                                               :modelValue="question.question"
                                               :error="question.question ? (question.question.length > 1000 ? $t('common.validationErrorText.lessCharacter', { length: '1,000' }) : '') : $t('userResearch.survey.create.error.questionTitle')"
                                               @input="onInputQuestion(question.index, 'question', $event)" />
                              </div>
                            </div>
                            <div v-if="question.questionType === 'multiple'"
                                 class="options">
                              <div class="multiple">
                                <ui-tooltip v-if="isEditing && question.questionId !== null && !isDraft"
                                            arrowCenter arrowBottom>
                                  {{ $t('userResearch.survey.create.noChange.form') }}
                                </ui-tooltip>
                                <button :disabled="isEditing && question.questionId !== null && !isDraft"
                                        @click="onInputQuestion(question.index, 'allowMultiple', !question.allowMultiple)">
                                  {{ $t('userResearch.survey.create.allowMultiple') }}
                                  <ui-checkbox :value="question.allowMultiple"
                                               :disabled="isEditing && question.questionId !== null && !isDraft" />
                                </button>
                              </div>

                              <ul class="option">
                                <li v-for="(option, i) in question.answerOptions"
                                    :key="`option-${index}-${i}`">
                                  <ui-checkbox v-if="question.allowMultiple"
                                               :disabled="true" />
                                  <ui-radio v-else
                                            :disabled="true" />
                                  <ui-input-text :type="'text'"
                                                 :id="`option-${index}-${i}`"
                                                 :placeholder="`${$t('userResearch.survey.create.option')} ${i + 1}`"
                                                 :modelValue="option"
                                                 :error="option ? (option.length > 100 ? $t('common.validationErrorText.lessCharacter', { length: '100' }) : '') : $t('userResearch.survey.create.error.option')"
                                                 @input="onInputQuestionOption(question.index, i, $event)" />
                                  <button :disabled="question.answerOptions.length <= 1"
                                          @click="onInputQuestionOptionDelete(question.index, i)">
                                    <i class="icon icon--trash-gray"></i>
                                  </button>
                                </li>
                                <li v-if="question.answerOther"
                                    class="other">
                                  <ui-checkbox v-if="question.allowMultiple"
                                               :disabled="true" />
                                  <ui-radio v-else
                                            :disabled="true" />
                                  <span class="other-text">{{ $t('userResearch.survey.create.other') }} :</span>
                                  <ui-input-text :type="'text'"
                                                 :placeholder="' '"
                                                 :disabled="true" />
                                  <button @click="onInputQuestion(question.index, 'answerOther', false)">
                                    <i class="icon icon--trash-gray"></i>
                                  </button>
                                </li>
                              </ul>

                              <div class="add-option">
                                <button @click="onInputQuestionOptionAdd(question.index)"
                                        :disabled="question.answerOptions.length + Number(!!question.answerOther) > 99">
                                  <i class="icon icon--plus"></i>
                                  <span>{{ $t('userResearch.survey.create.addOption') }}</span>
                                </button>
                                <button :disabled="question.answerOther || question.answerOptions.length + Number(!!question.answerOther) > 99"
                                        @click="onInputQuestion(question.index, 'answerOther', true)">
                                  <i class="icon icon--plus"></i>
                                  <span>{{ $t('userResearch.survey.create.addOther') }}</span>
                                </button>
                              </div>
                            </div>

                            <div v-if="question.questionType === 'essay'"
                                 class="options">
                              <textarea disabled="true"></textarea>
                            </div>

                            <div v-if="question.questionType === 'preference'"
                                 class="options">
                              <div class="preference description left">
                                <ui-input-text :type="'text'"
                                               :placeholder="$t('userResearch.survey.create.placeholder.preferenceDescription')"
                                               :modelValue="question.min.description"
                                               :error="question.min.description.length > 100 ? $t('common.validationErrorText.lessCharacter', { length: '100' }) : ''"
                                               @input="onInputQuestionPreference(question.index, 'min', 'description', $event)" />
                              </div>
                              <div class="preference number left">
                                <ui-tooltip v-if="isEditing && question.questionId !== null && !isDraft"
                                            arrowCenter arrowBottom>
                                  {{ $t('userResearch.survey.create.noChange.range') }}
                                </ui-tooltip>
                                <ui-selectbox :width="68"
                                              :value="question.min.value"
                                              :items="[0, 1]"
                                              :disabled="isEditing && question.questionId !== null && !isDraft"
                                              @input="onInputQuestionPreference(question.index, 'min', 'value', $event)" />
                              </div>
                              <div class="preference wave">
                                ~
                              </div>
                              <div class="preference number right">
                                <ui-tooltip v-if="isEditing && question.questionId !== null && !isDraft"
                                            arrowCenter arrowBottom>
                                  {{ $t('userResearch.survey.create.noChange.range') }}
                                </ui-tooltip>
                                <ui-selectbox :width="68"
                                              :value="question.max.value"
                                              :items="[5, 6, 7, 8, 9, 10]"
                                              :disabled="isEditing && question.questionId !== null && !isDraft"
                                              @input="onInputQuestionPreference(question.index, 'max', 'value', $event)" />
                              </div>
                              <div class="preference description right">
                                <ui-input-text :type="'text'"
                                               :placeholder="$t('userResearch.survey.create.placeholder.preferenceDescription')"
                                               :modelValue="question.max.description"
                                               :error="question.max.description.length > 100 ? $t('common.validationErrorText.lessCharacter', { length: '100' }) : ''"
                                               @input="onInputQuestionPreference(question.index, 'max', 'description', $event)" />
                              </div>
                            </div>
                          </div>
                        </template>
                      </transition-group>
                    </draggable>
                  </div>
                  <div class="add-question">
                    <button @click="addQuestion"
                            :disabled="computedQuestions.length > 99">
                      <i class="icon icon--plus"></i>
                      <span>{{ $t('userResearch.survey.create.addQuestion') }}</span>
                    </button>
                  </div>
                </div>

                <div class="step-item">
                  <ui-input-text :type="'textarea'"
                                 :placeholder="$t('userResearch.survey.create.placeholder.surveyClosing')"
                                 :modelValue="model.surveyClosing"
                                 :error="model.surveyClosing ? (model.surveyClosing.length > 1000 ? $t('common.validationErrorText.lessCharacter', { length: '1,000' }) : '') : $t('userResearch.survey.create.error.surveyClosing')"
                                 @input="onInput('surveyClosing', $event)">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.surveyClosing') }}
                      <i class="icon icon--required"></i>
                      <ui-icon-tooltip :iconSize="'outline-white'"
                                       arrowBottom
                                       arrowCenter>
                        <span v-html="$t('userResearch.survey.create.surveyClosingDescription')"></span>
                      </ui-icon-tooltip>
                    </h5>
                  </ui-input-text>
                </div>

                <div class="step-footer">
                  <ui-button v-if="!(isEditing && !isDraft)"
                             :primary2="true"
                             :disabled="!isStep1Complete"
                             @click="goStep(2)">
                    {{ $t('common.next') }}
                  </ui-button>
                </div>
              </div>
            </div>

            <div class="step step-2"
                 :class="{ ing: step === 2, opened: stepCompleted.step2 === true, editing: isEditing && !isDraft && isStep2Complete, complete: stepCompleted.step2 === true && isStep2Complete && !(isEditing && !isDraft), animating: animating === true }">
              <div class="step-header"
                   @click="goStep(2, true)">
                <span class="bullet">
                  <i class="icon icon--check-white-size16"></i>
                  <i class="number">2</i>
                </span>
                <h4>{{ $t('userResearch.survey.create.stepTitle.step2') }}</h4>
                <span v-if="isEditing && !isDraft"
                      class="arrow"
                      :class="{ disabled: !isStep2Complete }"></span>
                <button v-else
                        :disabled="!isStep2Complete"
                        class="next"
                        @click="goStep(3)">
                  {{ $t('common.next') }}
                </button>
              </div>
              <div class="step-body">
                <div class="step-item">
                  <div class="item-warning">
                    <i class="icon icon--warning"></i>
                    {{ $t('userResearch.survey.create.error.deviceIE') }}
                  </div>
                  <h5 class="item-title">
                    {{ $t('common.device2') }}
                    <span class="description">
                      {{ $t('userResearch.survey.create.error.device') }}
                    </span>
                  </h5>
                  <div class="item-checkbox-option">
                    <ui-checkbox v-for="item in deviceOptions"
                                 :key="`device-${item.value}`"
                                 :value="model.targetDevices.includes(item.value)"
                                 @input="toggleDevice(item.value)">
                      <span class="label">{{ item.text }}</span>
                    </ui-checkbox>
                  </div>
                </div>
                <div class="step-item">
                  <h5 class="item-title">
                    {{ $t('userResearch.survey.create.starting') }}
                  </h5>
                  <div class="item-line-option">
                    <span v-if="$i18n.locale === 'en'"
                          class="left">{{ $t('userResearch.survey.create.showSurvey') }}</span>
                    <ui-selectbox :width="235"
                                  :value="model.targetScope"
                                  :items="targetScopeOptions"
                                  @input="onInput('targetScope', $event)" />
                    <ui-selectbox :width="365"
                                  :value="model.popupLoading"
                                  :items="popupLoadingOptions"
                                  @input="onInput('popupLoading', $event)" />
                    <span v-if="$i18n.locale !== 'en'"
                          class="left">{{ $t('userResearch.survey.create.showSurvey') }}</span>

                    <div class="item-item match"
                         :class="{ show: model.targetScope !== SURVEY_CONSTANT.TARGET_SCOPE_ALL}">
                      <h6>
                        {{ $t('userResearch.survey.create.setSpecific') }}
                        <ui-icon-tooltip :iconSize="'outline-white'"
                                         arrowBottom
                                         arrowCenter>
                          <span v-html="$t('userResearch.survey.create.setSpecificDescription')"></span>
                        </ui-icon-tooltip>
                      </h6>

                      <div class="option-url">
                        <div class="url-match">
                          <ui-selectbox :width="245"
                                        :value="model.targetMatchType"
                                        :items="matchTypeOptions"
                                        @input="onInput('targetMatchType', $event)">
                            {{ $t('userResearch.survey.create.matchType') }}
                          </ui-selectbox>
                        </div>
                        <div class="url-analyze"
                             :class="{ short: !['exact', 'simple', 'start'].includes(model.targetMatchType) }">
                          <ui-input-text :type="'text'"
                                         :placeholder="['contains', 'end', 'regexp'].includes(model.targetMatchType) ? ' https://www.beusable.net' : (
                                           model.targetMatchType === 'simple' ? 'http://www.beusable.net/' : 'http://www.beusable.net/signup/?x=1'
                                         )"
                                         :modelValue="model.targetUrl"
                                         :modelError="model.targetUrl ? (isStep2TargetUrlComplete === null ? 'diffDomainUrl' : (isStep2TargetUrlComplete ? '' : 'diffFormatUrl')) : 'requiredUrl'"
                                         @input="onInput('targetUrl', $event)">

                            {{ ['contains', 'end', 'regexp'].includes(model.targetMatchType) ? $t('userResearch.survey.create.domainToAnalyze') : $t('userResearch.survey.create.urlToAnalyze') }}
                          </ui-input-text>
                        </div>
                        <div class="url-regexp"
                             :class="{ hidden: ['exact', 'simple', 'start'].includes(model.targetMatchType)}">
                          <ui-input-text :type="'text'"
                                         :placeholder="matchOptions[model.targetMatchType].matchTextPlaceholder"
                                         :modelValue="model.targetMatchString"
                                         @input="onInput('targetMatchString', $event)">
                            {{ matchOptions[model.targetMatchType].matchText }}
                          </ui-input-text>
                        </div>
                      </div>

                      <template v-if="model.targetMatchType === 'simple'">
                        <span v-if="$i18n.locale === 'en' && model.targetScope === 'include'"
                              class="left">{{ $t('userResearch.survey.create.showSurveyTo') }}</span>
                        <span v-if="$i18n.locale === 'en' && model.targetScope === 'exclude'"
                              class="left">{{ $t('userResearch.survey.create.showSurveyNotTo') }}</span>
                        <ui-selectbox :width="245"
                                      :value="model.customer"
                                      :items="customerOptions"
                                      @input="onInput('customer', $event)" />
                        <span v-if="$i18n.locale !== 'en' && model.targetScope === SURVEY_CONSTANT.TARGET_SCOPE_INCLUDE"
                              class="right">{{ $t('userResearch.survey.create.showSurveyTo') }}</span>
                        <span v-if="$i18n.locale !== 'en' && model.targetScope === SURVEY_CONSTANT.TARGET_SCOPE_EXCLUDE"
                              class="right">{{ $t('userResearch.survey.create.showSurveyNotTo') }}</span>
                      </template>
                    </div>

                    <div class="item-item utm"
                         :class="{ show: model.customer === 'campaign' }">
                      <h6 class="utm">
                        {{ $t('userResearch.survey.create.setUtm') }}
                        <ui-icon-tooltip :iconSize="'outline-white'"
                                         arrowBottom
                                         arrowCenter>
                          <span v-html="$t('userResearch.survey.create.setUtmDescription')"
                                style="display: block; text-align: left"></span>
                        </ui-icon-tooltip>
                        <span class="item-description with-icon"
                              v-if="!isStep2UtmComplete">
                          {{ $t('userResearch.survey.create.error.least1Utm') }}
                        </span>
                      </h6>

                      <ul class="utm-options">
                        <li v-for="parameter in utmParameters"
                            :key="`utm-${parameter.value}`">
                          <span class="checkbox">
                            <ui-checkbox :value="model.campaignParams[parameter.value].enable"
                                         @input="onInputUtm(parameter.value, 'enable', !model.campaignParams[parameter.value].enable)" />
                          </span>
                          <span class="label">
                            {{ parameter.text }}
                          </span>
                          <span class="value">
                            <ui-input-text :type="'text'"
                                           :placeholder="$t('userResearch.survey.create.placeholder.utm')"
                                           :disabled="!model.campaignParams[parameter.value].enable"
                                           :modelValue="model.campaignParams[parameter.value].value"
                                           :error="model.campaignParams[parameter.value].enable && !model.campaignParams[parameter.value].value ? $t('userResearch.survey.create.error.utm') : ''"
                                           @input="onInputUtm(parameter.value, 'value', $event)" />
                          </span>
                          <span class="condition">
                            <ui-selectbox :width="215"
                                          :disabled="!model.campaignParams[parameter.value].enable"
                                          :value="model.campaignParams[parameter.value].matchType"
                                          :items="utmConditionOptions"
                                          @input="onInputUtm(parameter.value, 'matchType', $event)" />
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="step-item">
                  <div class="item-validation">
                    <div class="vali-title">
                      <h6>
                        {{ $t('userResearch.survey.create.checkUrl') }}
                      </h6>
                      <p v-html="$t('userResearch.survey.create.checkUrlDescription')">
                      </p>
                    </div>
                    <div class="vali-value">
                      <ui-input-text :type="'text'"
                                     :placeholder="'http://www.beusable.net/signup/?source=google'"
                                     :modelValue="model.validationUrl"
                                     @input="onInput('validationUrl', $event)" />
                      <ui-button @click="onValidateUrl"
                                 :disabled="!model.validationUrl">
                        {{ $t('userResearch.survey.create.check') }}
                      </ui-button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="step-footer">
                <ui-button v-if="!(isEditing && !isDraft)"
                           :primary2="true"
                           :disabled="!isStep2Complete"
                           @click="goStep(3)">
                  {{ $t('common.next') }}
                </ui-button>
              </div>
            </div>

            <div class="step step-3"
                 :class="{ ing: step === 3, opened: stepCompleted.step3 === true, editing: isEditing && !isDraft && isStep3Complete, complete: stepCompleted.step3 === true && isStep3Complete && !(isEditing && !isDraft), animating: animating === true }">
              <div class="step-header"
                   @click="goStep(3, true)">
                <span class="bullet">
                  <i class="icon icon--check-white-size16"></i>
                  <i class="number">3</i>
                </span>
                <h4>{{ $t('userResearch.survey.create.stepTitle.step3') }}</h4>
                <span v-if="isEditing && !isDraft"
                      class="arrow"
                      :class="{ disabled: !isStep3Complete }"></span>
                <button v-else
                        :disabled="!isStep3Complete"
                        class="next"
                        @click="goStep(4)">
                  {{ $t('common.next') }}
                </button>
              </div>
              <div class="step-body">
                <div class="step-left">
                  <div class="step-item">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.popupStyle') }}
                      <span :class="`description ${$i18n.locale}`">
                        {{ $t('userResearch.survey.create.popupStyleDescription') }}
                      </span>
                    </h5>
                    <div class="item-checkbox-option popup">
                      <div class="radio-popup">
                        <ui-radio :value="model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS"
                                  @input="onInput('popupStyle', SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS)">
                          {{ $t('userResearch.survey.create.popover') }}
                          <ui-icon-tooltip :iconSize="'outline-white'"
                                           arrowBottom
                                           arrowCenter>
                            <span v-html="$t('userResearch.survey.create.popoverDescription')"></span>
                          </ui-icon-tooltip>
                        </ui-radio>

                        <div class="image popover"
                             @click="onInput('popupStyle', SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS)"></div>

                        <transition name="fade">
                          <ui-selectbox v-show="model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS"
                                        :width="110"
                                        :value="model.popupLocation"
                                        :items="popoverOptions"
                                        @input="onInput('popupLocation', $event)" />
                        </transition>
                      </div>

                      <div class="radio-popup">
                        <ui-radio :value="model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME"
                                  @input="onInput('popupStyle', SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME)">
                          <span class="two-line">
                            <span v-html="$t('userResearch.survey.create.fullScreen')"></span>
                            <ui-icon-tooltip :iconSize="'outline-white'"
                                             arrowBottom
                                             arrowCenter>
                              <span v-html="$t('userResearch.survey.create.fullScreenDescription')"></span>
                            </ui-icon-tooltip>
                          </span>
                        </ui-radio>

                        <div class="image fullscreen"
                             @click="onInput('popupStyle', SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME)"></div>
                      </div>
                    </div>
                  </div>
                  <div class="step-item">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.language') }}
                      <span class="description">
                        {{ $t('userResearch.survey.create.languageDescription') }}
                      </span>
                    </h5>
                    <div class="item-checkbox-option language">
                      <div class="radio-language">
                        <ui-radio :value="languageType === SURVEY_CONSTANT.LANG_TYPE_BOWSER"
                                  @input="onChangeLanguageType(SURVEY_CONSTANT.LANG_TYPE_BOWSER)">
                          <span v-html="$t('userResearch.survey.create.languageType.browser')"></span>
                          <ui-icon-tooltip :iconSize="'outline-white'"
                                           arrowBottom
                                           arrowCenter>
                            <span v-html="$t('userResearch.survey.create.languageType.toolTipBrowser')"></span>
                          </ui-icon-tooltip>
                        </ui-radio>
                      </div>
                      <div class="radio-language">
                        <ui-radio :value="languageType === SURVEY_CONSTANT.LANG_TYPE_FIX"
                                  @input="onChangeLanguageType(SURVEY_CONSTANT.LANG_TYPE_FIX)">
                          <span v-html="$t('userResearch.survey.create.languageType.fix')"></span>
                          <ui-icon-tooltip :iconSize="'outline-white'"
                                           arrowBottom
                                           arrowCenter>
                            <span v-html="$t('userResearch.survey.create.languageType.toolTipFix')"></span>
                          </ui-icon-tooltip>
                        </ui-radio>
                        <transition name="fade">
                          <ui-selectbox v-show="languageType === SURVEY_CONSTANT.LANG_TYPE_FIX"
                                        :width="130"
                                        :value="model.lang"
                                        :items="languageOptions"
                                        @input="onInput('lang', $event)" />
                        </transition>
                      </div>
                    </div>
                  </div>
                  <div class="step-item">
                    <h5 class="item-title">
                      {{ $t('userResearch.survey.create.color') }}
                    </h5>
                    <div class="item-checkbox-option color">
                      <ui-radio v-for="color in colorOptions"
                                :key="`key${color}`"
                                :value="model.popupColor === color"
                                @input="onInput('popupColor', color)">
                        <span class="color-chip"
                              :class="[`color-${color.replace('#', '')}`]"
                              :style="{ 'border-color': color }">
                          <span class="color-pane"
                                :style="{ 'background-color': color }"></span>
                        </span>
                      </ui-radio>
                    </div>
                  </div>
                </div>
                <div class="step-right">

                  <div class="item-preview"
                        :class="[`preview-${model.popupLocation}`,
                                 model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS ? 'preview-popup-style' : 'preview-popup-modal',
                                 previewing === 'phone' ? 'phone' : 'desktop' ]">
                    <div class="preview-header"
                         :class="{ bottom: model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME && previewing === 'phone' }">
                      <ba-button v-for="item in deviceOptions"
                                 :key="`device-preview-${item.value}`"
                                 :icon="`icon--device ${item.value}`"
                                 :iconHover="`icon--device ${item.value} hover`"
                                 :iconActive="`icon--device ${item.value} on`"
                                 :active="previewing == item.value"
                                 :disabled="!model.targetDevices.includes(item.value)"
                                 @click="previewing = item.value">
                      </ba-button>
                    </div>
                    <div class="preview-body"
                         :class="[`preview-${model.popupColor.replace('#', '')}`]">
                      <div v-if="model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS"
                           class="preview-popover"
                           :class="[`preview-${model.popupLocation}`,
                                    previewing === 'phone' ? 'phone' : 'desktop',
                                    { hidden: !previewToggle }]">
                        <div class="pr-header">
                          <button @click="togglePreviewLayer">
                            <div class="preview-hide-bg-radius"></div>
                            <span>{{ $tc('userResearch.survey.create.previewing.survey', previewingLanguage) }}</span>
                            <i class="icon icon--arrow-up-black"></i>
                          </button>
                        </div>
                        <div class="pr-body">
                          <div class="pr-question">
                               <span v-html="$tc('userResearch.survey.create.previewing.question', previewingLanguage)"></span>
                               <span class="pr-question-pagging">(1/6)</span>
                          </div>
                          <div class="pr-option">
                            <ui-radio :value="true">
                              {{ $tc('userResearch.survey.create.previewing.option1', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option2', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option3', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option4', previewingLanguage) }}
                            </ui-radio>
                          </div>
                          <div class="pr-button">

                            <button class="pr-button-prev">
                              <i class="icon icon--arrow-up-black"></i>
                            </button>
                            <button class="pr-button-next">
                              {{ $tc('userResearch.survey.create.previewing.reply', previewingLanguage) }}
                              <i class="icon icon--arrow-up-black"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <div v-if="model.popupStyle === SURVEY_CONSTANT.POPUP_STYLE_MODAL_ONETIME"
                           class="preview-fullscreen"
                           :class="[`preview-${model.popupLocation}`,
                                    previewing === 'phone' ? 'phone' : 'desktop',
                                    { hidden: !previewToggle }]">
                        <div class="pr-header">
                          <button @click="togglePreviewLayer">
                            <i class="icon icon--close-size16"></i>
                          </button>
                        </div>
                        <div class="pr-body">
                          <div class="pr-question">
                              <span v-html="$tc('userResearch.survey.create.previewing.question', previewingLanguage)"></span>
                              <span class="pr-question-pagging">(1/6)</span>
                          </div>
                          <div class="pr-option">
                            <ui-radio :value="true">
                              {{ $tc('userResearch.survey.create.previewing.option1', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option2', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option3', previewingLanguage) }}
                            </ui-radio>
                            <ui-radio :value="false">
                              {{ $tc('userResearch.survey.create.previewing.option4', previewingLanguage) }}
                            </ui-radio>
                          </div>
                          <div class="pr-button">
                            <button class="pr-button-prev">
                              <i class="icon icon--arrow-up-black"></i>
                            </button>
                            <button class="pr-button-next">
                              {{ $tc('userResearch.survey.create.previewing.reply', previewingLanguage) }}
                              <i class="icon icon--arrow-up-black"></i>
                            </button>
                            <button class="pr-button-skip"><span class="txt_skip" v-html="$tc('userResearch.survey.create.previewing.skip', previewingLanguage)"></span></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="step-footer">
                <ui-button v-if="!(isEditing && !isDraft)"
                           :primary2="true"
                           :disabled="!isStep3Complete"
                           @click="goStep(4)">
                  {{ $tc('common.next', previewingLanguage) }}
                </ui-button>
              </div>
            </div>

            <div class="step step-4"
                 :class="{ ing: step === 4, opened: stepCompleted.step4 === true, editing: isEditing && !isDraft && isStep4Complete, complete: stepCompleted.step4 === true && isStep4Complete && !(isEditing && !isDraft), animating: animating === true }">
              <div class="step-header"
                   @click="goStep(4, true)">
                <span class="bullet">
                  <i class="icon icon--check-white-size16"></i>
                  <i class="number">4</i>
                </span>
                <h4>{{ $t('userResearch.survey.create.stepTitle.step4') }}</h4>
                <span v-if="isEditing && !isDraft"
                      class="arrow"
                      :class="{ disabled: !isStep4Complete }"></span>
              </div>
              <div class="step-body">
                <div class="step-item">
                  <h5 class="item-title">
                    {{ $t('userResearch.survey.create.startDate') }}
                  </h5>
                  <div v-if="isEditing && !isDraft && [SURVEY_CONSTANT.STATUS_WAITING, SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_STOP, SURVEY_CONSTANT.STATUS_END].includes(survey.status) && survey.startDate <= today"
                       class="item-fixed">
                    {{ survey.startDate | dashDate }}
                  </div>
                  <div v-else
                       class="item-checkbox-option">
                    <ui-radio :value="model.start === 'now'"
                              @input="onInput('start', 'now')">
                      {{ $t('userResearch.survey.create.startNow') }}
                    </ui-radio>
                    <ui-radio :value="model.start !== 'now'"
                              @input="onInput('start', calendarStartDate || today)">
                      {{ $t('userResearch.survey.create.chooseDate') }}
                    </ui-radio>
                    <transition name="fade">
                      <ui-input-date v-show="model.start !== 'now'"
                                     :modelValue="calendarStartDate"
                                     :reset="false"
                                     :minDate="startMinDate"
                                     :timezone="domain.timezone"
                                     :timezoneName="domain.timezoneName"
                                     :id="'starDate'"
                                     @input="onInput('start', $event)" />
                    </transition>
                  </div>
                </div>

                <div class="step-item">
                  <h5 class="item-title">
                    {{ $t('userResearch.survey.create.endDate') }}
                    <span class="description">
                      {{ $t('userResearch.survey.create.endDateDescription') }}
                    </span>
                  </h5>
                  <div class="item-checkbox-option">
                    <ui-radio :value="model.end === 'after14'"
                              @input="onInput('end', 'after14')">
                      {{ $t('userResearch.survey.create.14daysLater') }}
                    </ui-radio>
                    <ui-radio :value="model.end === 'after30'"
                              @input="onInput('end', 'after30')">
                      {{ $t('userResearch.survey.create.30daysLater') }}
                    </ui-radio>
                    <ui-radio :value="model.end !== 'after14' && model.end !== 'after30'"
                              @input="onInput('end', calendarEndDate || today)">
                      {{ $t('userResearch.survey.create.chooseDate') }}
                    </ui-radio>
                    <transition name="fade">
                      <ui-input-date v-show="model.end !== 'after14' && model.end !== 'after30'"
                                     :modelValue="calendarEndDate"
                                     :reset="false"
                                     :id="'endDate'"
                                     :minDate="endMinDate"
                                     :timezone="domain.timezone"
                                     :timezoneName="domain.timezoneName"
                                     @input="onInput('end', $event)" />
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="add-footer">
            <span v-if="isDraft || !isEditing"
                  class="button-container">
              <ui-button :disabled="!model.surveyName"
                         @click="onConfirm(true)">
                {{ $t('userResearch.survey.create.saveDraft') }}
              </ui-button>
              <transition name="fade">
                <div v-if="saveDraft"
                     class="toast">
                  {{ $t('userResearch.survey.create.savedDraft') }}
                  <i class="icon icon--check-green-size16"></i>
                </div>
              </transition>
            </span>
            <span class="button-container">
              <ui-button :primary2="true"
                         :disabled="!isAllComplete"
                         @click="onPreview">
                {{ $t('userResearch.survey.create.preview') }}
              </ui-button>
            </span>
            <span class="button-container">
              <ui-tooltip v-if="requireNoRunning"
                          class="button-tooltip"
                          arrowCenter
                          arrowBottom>
                <span v-html="$t('userResearch.survey.create.hasRunningTooltip')"></span>
              </ui-tooltip>
              <ui-button :primary="true"
                         :disabled="requireNoRunning || !isAllComplete"
                         @click="onConfirm">
                <template v-if="isEditing && !isDraft">
                  {{ $t('userResearch.survey.create.edit') }}
                </template>
                <template v-else>
                  {{ $t('userResearch.survey.create.registration') }}
                </template>
              </ui-button>
            </span>

            <div class="cant-preview">
              {{ $t('userResearch.survey.create.cantPreview.link') }}
              <em @click="previewHelp = true">
                {{ $t('userResearch.survey.create.cantPreview.help') }}
              </em>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <dimmed v-if="alert.notTarget">
      <alert :title="$t('userResearch.survey.create.alert.notTarget.title')"
             :description="$t('userResearch.survey.create.alert.notTarget.description')"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('notTarget')" />
    </dimmed>

    <dimmed v-if="alert.diffDomainUrl">
      <alert :title="$t('userResearch.survey.create.alert.diffDomainUrl.title')"
             :description="$t('userResearch.survey.create.alert.diffDomainUrl.description')"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('diffDomainUrl')" />
    </dimmed>

    <dimmed v-if="alert.warnInstall">
      <alert :title="$t('userResearch.survey.create.alert.warnInstall.title')"
             :description="$t('userResearch.survey.create.alert.warnInstall.description')"
             :icon="'icon--document-error'"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('warnInstall')" />
    </dimmed>

    <dimmed v-if="alert.noInstall">
      <alert :title="$t('userResearch.survey.create.alert.noInstall.title')"
             :description="$t('userResearch.survey.create.alert.noInstall.description')"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('noInstall')" />
    </dimmed>

    <dimmed v-if="alert.validated">
      <alert :title="$t('userResearch.survey.create.alert.validated.title')"
             :icon="'icon--check-green-size50'"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('validated')" />
    </dimmed>

    <dimmed v-if="alert.saveBeforePreview">
      <alert :title="$t('userResearch.survey.create.alert.saveBeforePreview.title')"
             :description="$t('userResearch.survey.create.alert.saveBeforePreview.description')"
             :icon="'icon--document-error'"
             :submit="$t('userResearch.survey.create.alert.saveBeforePreview.confirm')"
             :cancel="$t('common.cancel')"
             @onSubmit="onSaveBeforePreview"
             @onCancel="toggleAlert('saveBeforePreview')" />
    </dimmed>

    <dimmed v-if="alert.checkTempData">
      <alert :title="$t('userResearch.survey.create.alert.checkTempData.title')"
             :submit="$t('common.yes')"
             :cancel="$t('common.no')"
             @onSubmit="onTempData(true)"
             @onCancel="onTempData(false)" />
    </dimmed>

    <dimmed v-if="alert.deletingQuestion">
      <alert :title="$t('userResearch.survey.create.alert.deletingQuestion.title')"
             :description="$t('userResearch.survey.create.alert.deletingQuestion.description')"
             :submit="$t('common.confirm')"
             :cancel="$t('common.no')"
             @onSubmit="confirmDeleteQuestion(deletingQuestion)"
             @onCancel="toggleAlert('deletingQuestion')" />
    </dimmed>

    <dimmed v-if="alert.deletingOption">
      <alert :title="$t('userResearch.survey.create.alert.deletingOption.title')"
             :description="$t('userResearch.survey.create.alert.deletingOption.description')"
             :submit="$t('common.confirm')"
             :cancel="$t('common.no')"
             @onSubmit="onConfirmQuestionOptionDelete(deletingQuestion, deletingOption)"
             @onCancel="toggleAlert('deletingOption')" />
    </dimmed>

    <dimmed v-if="alert.noPreviewForIe">
      <alert :title="$t('userResearch.survey.create.alert.noPreviewForIe.title')"
             :description="$t('userResearch.survey.create.alert.noPreviewForIe.description')"
             :submit="$t('common.confirm')"
             @onSubmit="toggleAlert('noPreviewForIe')" />
    </dimmed>

    <dimmed v-if="previewHelp">
      <div class="preview-help">
        <i class="icon icon--close-size16"
           @click="previewHelp = false"></i>
        <div class="help-header">
          <h4 v-html="$t('userResearch.survey.create.cantPreview.title')"></h4>
        </div>
        <div class="help-body">
          <h5 v-html="$t('userResearch.survey.create.cantPreview.title1')"></h5>
          <p v-html="$t('userResearch.survey.create.cantPreview.description1')"></p>
          <h5 v-html="$t('userResearch.survey.create.cantPreview.title2')"></h5>
          <p v-html="$t('userResearch.survey.create.cantPreview.description2')"></p>
          <h5 v-html="$t('userResearch.survey.create.cantPreview.title3')"></h5>
          <p v-html="$t('userResearch.survey.create.cantPreview.description3')"></p>
          <h5 v-html="$t('userResearch.survey.create.cantPreview.title4')"></h5>
          <p v-html="$t('userResearch.survey.create.cantPreview.description4')"></p>
        </div>
        <div class="help-footer">
          <p v-html="$t('userResearch.survey.create.cantPreview.footer')"></p>
          <div class="email">
            <i class="icon icon--mail-size16"></i>
            <a :href="`mailto:${$t('userResearch.survey.create.cantPreview.email')}`">{{ $t('userResearch.survey.create.cantPreview.email') }}
            </a>
          </div>
        </div>
      </div>
    </dimmed>

    <dimmed v-if="previewModal">
      <div class="preview-modal">
        <i class="icon icon--close-size16"
           @click="previewModal = false"></i>
        <div class="modal-header">
          <i class="icon icon--screens"></i>
          <h4 v-html="$t('userResearch.survey.create.alert.urlToPreview.title')"></h4>
          <p v-html="$t('userResearch.survey.create.alert.urlToPreview.description')"></p>
        </div>
        <div class="modal-body">
          <ui-input-text :type="'text'"
                         :placeholder="'https://www.beusable.net/ko/'"
                         :modelValue="previewUrl"
                         :modelError="previewUrlError"
                         @input="onInputPreview">
            {{ $t('common.url') }}
          </ui-input-text>
        </div>
        <div class="modal-footer">
          <ui-button :primary="true"
                     :iconName="'icon--ba-link white'"
                     @click="onConfirmPreview">
            {{ $t('userResearch.survey.create.alert.urlToPreview.confirm') }}
          </ui-button>
        </div>
      </div>
    </dimmed>

    <dimmed v-if="isRunningSurvey">
      <alert :title="$t('userResearch.survey.alert.run.title')"
             :description="$t('userResearch.survey.alert.run.description')"
             :icon="'icon--dialog-warning'"
             :submit="$t('common.confirm')"
             @onSubmit="isRunningSurvey = false"
      />
    </dimmed>

    <dimmed v-if="isSurveyPrivacyExample">
      <div class="privacy-modal">
        <i class="icon icon--close-size16"
           @click="isSurveyPrivacyExample = false"></i>
        <div class="modal-header">
          <i class="icon icon--document-write"></i>
          <h4 v-html="$t('userResearch.survey.create.privacy.exampleContent.title')"></h4>
        </div>
        <div class="modal-body" id="privacyExample" v-html="$t('userResearch.survey.create.privacy.exampleContent.content')">
        </div>
        <div class="modal-footer">
          <ui-button :primary="true"
                     @click="onPrivacyExampleImport">
            {{ $t('userResearch.survey.create.privacy.exampleContent.import') }}
          </ui-button>
        </div>
      </div>
    </dimmed>

    <transition name="fade">
      <ba-loading v-if="partLoading"
                  :applyBackground="true"
                  :showLoadingText="true" />
    </transition>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { mapState, mapMutations } from 'vuex'
import { format, addDays, parse, isValid } from 'date-fns'
import { EventBus } from '@/libs/event-bus'
import { getData, checkInstall } from '@/api/domain'
import { utcToLocalTimezone, isSameDomain, urlSearchParam } from '@/libs/utils'
import {
  getSurveyList,
  getSurvey,
  createSurvey,
  updateSurvey,
  getSurveyPalette,
  checkInRanking,
  checkTempData,
  deleteSurvey,
  getQuestionCountByDelete
} from '@/api/survey'

import UiIconTooltip from '@/components/ui/UiIconTooltip'
import UiTooltip from '@/components/ui/UiTooltip'
import UiInputText from '@/components/ui/UiInputText'
import UiInputDate from '@/components/ui/UiInputDate'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiRadio from '@/components/ui/UiRadio'
import UiButton from '@/components/ui/UiButton'
import BaButton from '@/components/ui/ba/Button'
import BaLoading from '@/components/ui/ba/Loading'
import Dimmed from '@/components/ui/Dimmed'
import Alert from '@/components/ui/Alert'
import BaSwitch from '@/components/ui/ba/Switch'
import { SURVEY_CONSTANT } from '@/libs/constants'
import { getDialog } from '@/libs/vue-utils'
import { isInIframe } from '@/libs/auth-helper'

const openDialog = getDialog('dialogType')

export default {
  name: 'AddSurvey',
  components: {
    draggable,
    UiIconTooltip,
    UiTooltip,
    UiInputDate,
    UiInputText,
    UiSelectbox,
    UiCheckbox,
    UiRadio,
    UiButton,
    BaButton,
    BaLoading,
    Dimmed,
    Alert,
    BaSwitch
  },
  async created () {
    await this.load()
    this.calendarStartDate = format(addDays(utcToLocalTimezone(new Date(), this.domain.timezoneName), 1), 'YYYY-MM-DD')
    this.calendarEndDate = format(addDays(utcToLocalTimezone(new Date(), this.domain.timezoneName), 2), 'YYYY-MM-DD')
    this.today = format(utcToLocalTimezone(new Date(), this.domain.timezoneName), 'YYYY-MM-DD')
  },
  mounted () {
    document.getElementById('dashboardLayout').style.minHeight = 'auto'
  },
  methods: {
    ...mapMutations([
      'setSurveyList',
      'setSurveyDetail'
    ]),
    onChangeLanguageType (value) {
      if (this.languageType !== value) {
        this.languageType = value
        this.onInput('lang', value === SURVEY_CONSTANT.LANG_TYPE_FIX ? this.$i18n.locale : '')
      }
    },
    onPrivacyExampleImport () {
      const example = document.querySelector('#privacyExample').textContent
      this.model.privacyAgrmt = example
      this.isSurveyPrivacyExample = false
    },
    openPrivacyExample () {
      this.isSurveyPrivacyExample = true
    },
    async load () {
      let sid = this.$route.query.sid
      let surveyId = this.$route.query.survey
      let duplicateId = this.$route.query.duplicate
      let tempSurveyId = this.$route.query.tempSurveyId
      let tempDuplicateId = this.$route.query.duplicateTemp

      if (this.loading) {
        getSurveyList({ sid }, true).then(result => {
          this.setSurveyList([sid, result])
          this.surveys = []
          for (const survey of result) {
            this.surveys.push(survey)
          }
          this.calcHasRunning()
        })
        // if (!this.surveyList || !this.surveyList[sid]) {
        //   getSurveyList({ sid }, true).then(result => {
        //     this.setSurveyList([sid, result])
        //     this.surveys = []
        //     for (const survey of result) {
        //       this.surveys.push(survey)
        //     }
        //     this.calcHasRunning()
        //   })
        // } else {
        //   this.surveys = []
        //   for (const survey of this.surveyList[sid]) {
        //     if (survey) {
        //       this.surveys.push(survey)
        //     }
        //   }
        //   this.calcHasRunning()
        // }

        await getData(sid).then(result => {
          this.domain = {
            sid: result.orgSid || result.sid,
            orgSid: result.orgSid,
            realUrl: result.realUrl || result.domain,
            domain: result.domain,
            domainName: result.domainName,
            timezone: result.timezone,
            timezoneName: result.timezoneName
          }
        })

        getSurveyPalette().then(result => {
          this.colorOptions = []
          for (const color of result) {
            this.colorOptions.push(color.colorHex)
          }
        })
      }

      if (surveyId) {
        if (this.useTemp) {
          checkTempData({
            sid,
            surveyId
          }, true).then(result => {
            if (result) {
              this.tempData = {
                ...result,
                surveyId
              }
              this.toggleAlert('checkTempData')
            } else {
              let key = `${sid}-${surveyId}`

              getSurvey({
                sid,
                surveyId
              }, true).then(result => {
                this.afterLoad(result)
                this.setSurveyDetail([key, result])
              })

              // if (!this.surveyDetail[key]) {
              //   getSurvey({
              //     sid,
              //     surveyId
              //   }, true).then(result => {
              //     this.afterLoad(result)
              //     this.setSurveyDetail([key, result])
              //   })
              // } else {
              //   this.afterLoad(this.surveyDetail[key])
              // }
            }
          })
        } else {
          let key = `${sid}-${surveyId}`
          getSurvey({
            sid,
            surveyId
          }, true).then(result => {
            this.afterLoad(result)
            this.setSurveyDetail([key, result])
          })

          // if (!this.surveyDetail[key]) {
          //   getSurvey({
          //     sid,
          //     surveyId
          //   }, true).then(result => {
          //     this.afterLoad(result)
          //     this.setSurveyDetail([key, result])
          //   })
          // } else {
          //   this.afterLoad(this.surveyDetail[key])
          // }
        }
        this.domainValidated = true
      } else if (tempSurveyId) {
        let key = `${sid}-temp-${tempSurveyId}`
        getSurvey({
          sid,
          tempSurveyId,
          isTemp: true
        }, true).then(result => {
          this.afterLoad(result, true)
          this.setSurveyDetail([key, result])
        })

        // if (!this.surveyDetail[key]) {
        //   getSurvey({
        //     sid,
        //     tempSurveyId,
        //     isTemp: true
        //   }, true).then(result => {
        //     this.afterLoad(result, true)
        //     this.setSurveyDetail([key, result])
        //   })
        // } else {
        //   this.afterLoad(this.surveyDetail[key], true)
        // }
      } else if (duplicateId) {
        EventBus.$emit('setTitle', this.$t('userResearch.survey.create.title'))
        this.step = 1

        getSurvey({
          sid,
          surveyId: duplicateId
        }, true).then((result) => {
          this.setModel(result, true)
          this.loading = false
          setTimeout(() => {
            EventBus.$emit('onLoading', false)
          }, 300)
        })
      } else if (tempDuplicateId) {
        EventBus.$emit('setTitle', this.$t('userResearch.survey.create.title'))
        this.step = 1

        getSurvey({
          sid,
          tempSurveyId: tempDuplicateId,
          isTemp: true
        }).then((result) => {
          this.setModel(result, true)
          this.loading = false
          setTimeout(() => {
            EventBus.$emit('onLoading', false)
          }, 300)
        })
      } else {
        EventBus.$emit('setTitle', this.$t('userResearch.survey.create.title'))
        this.model.targetMatchType = 'exact'
        this.model.customer = 'all'
        this.step = 1

        this.addQuestion(false)
        this.loading = false
        setTimeout(() => {
          EventBus.$emit('onLoading', false)
        }, 300)
      }
    },
    changeIsSurveyPrivacy () {
      if (this.isSurveyPrivacy) {
        this.model.privacyAgrmt = ''
      }
      this.isSurveyPrivacy = !this.isSurveyPrivacy
    },
    afterLoad (result, temp = false) {
      if (result.surveyId && !this.useTemp) {
        delete result.tempSurveyId
      }
      if (result.stepCompleted) {
        this.stepCompleted = { ...result.stepCompleted }
        delete result.stepCompleted
      }

      if (result.privacyAgrmt !== '') {
        this.isSurveyPrivacy = true
      }

      this.deletedQuestionsList = JSON.parse(JSON.stringify(result.questions))
      this.deletedQuestionsList = this.deletedQuestionsList.filter(item => {
        return item.deleted
      })

      result.questions = result.questions.filter(item => {
        return !item.deleted
      })

      if (result.lang === '') {
        this.languageType = SURVEY_CONSTANT.LANG_TYPE_BOWSER
      } else {
        this.languageType = SURVEY_CONSTANT.LANG_TYPE_FIX
      }

      this.survey = result
      this.setModel(result, false, temp)
      if (!this.isDraft) {
        EventBus.$emit('onLoading', true)
        EventBus.$emit('setTitle', this.$t('userResearch.survey.create.titleEdit'))
        setTimeout(() => {
          this.setStepCompleted(1, true)
          this.setStepCompleted(2, true)
          this.setStepCompleted(3, true)
          this.setStepCompleted(4, true)
          setTimeout(() => {
            this.loading = false
            EventBus.$emit('onLoading', false)
          }, 500)
        }, 100)
      } else {
        this.step = 1
        EventBus.$emit('setTitle', this.$t('userResearch.survey.create.title'))
        setTimeout(() => {
          this.loading = false
          EventBus.$emit('onLoading', false)
        }, 300)
      }

      // if (result.surveyId) {
      //   this.hasAnswer = {}
      //   getResponseSummary({
      //     sid: this.$route.query.sid,
      //     surveyId: result.surveyId,
      //     start: format(result.startDate, 'YYYY-MM-DD'),
      //     end: format(result.endDate, 'YYYY-MM-DD')
      //   }).then(response => {
      //     for (const q of Object.entries(response.statsPerQuestion)) {
      //       if (q[1].totalAnswered > 0) {
      //         const key = Number(q[0])
      //         this.hasAnswer[key] = true
      //         if (['radio', 'checkbox'].includes(q[1].qustionType)) {
      //           this.hasAnswer[key] = []
      //           for (const a of Object.entries(q[1].answers)) {
      //             if (a[1] > 0) {
      //               this.hasAnswer[key].push(Number(a[0]))
      //             }
      //           }
      //         }
      //       }
      //     }
      //   })
      // }
    },
    toDashboard () {
      openDialog('survey.editCancel')
        .then(result => {
          if (result) {
            EventBus.$emit('onLoading', true)
            setTimeout(() => {
              this.$router.push({ name: 'userResearch', query: { sid: this.$route.query.sid } })
            }, 300)
          }
        })
    },
    goStep (step, fromHeader = false) {
      if (this.isEditing && !this.isDraft) {
        if ((this.step === 1 && !this.isStep1Complete) ||
          (this.step === 2 && !this.isStep2Complete) ||
          (this.step === 3 && !this.isStep3Complete) ||
          (this.step === 4 && !this.isStep4Complete)) {
          return
        }

        if (this.step === step) {
          if ((this.step === 1 && this.isStep1Complete) ||
            (this.step === 2 && this.isStep2Complete) ||
            (this.step === 3 && this.isStep3Complete) ||
            (this.step === 4 && this.isStep4Complete)) {
            this.step = 0
            this.animating = true
            setTimeout(() => {
              this.animating = false
            }, 500)
            return
          }
        }
      }

      if (!fromHeader) {
        setTimeout(() => {
          this.goStep(step, true)
        }, 100)
        return
      }

      if (this.step !== step) {
        if (this.stepCompleted[`step${step}`] || this.step >= step - 1 || (this.isEditing && !this.isDraft) || (
          (step === 4 && this.isStep3Complete && this.isStep2Complete && this.isStep1Complete && this.stepCompleted[`step3`] && this.stepCompleted[`step2`] && this.stepCompleted[`step1`]) ||
          (step === 3 && this.isStep2Complete && this.isStep1Complete && this.stepCompleted[`step2`] && this.stepCompleted[`step1`]) ||
          (step === 2 && this.isStep1Complete && this.stepCompleted[`step1`]) ||
          step === 1)) {
          if (this.step === 0 ||
            (this.step === 1 && this.isStep1Complete) ||
            (this.step === 2 && this.isStep2Complete) ||
            (this.step === 3 && this.isStep3Complete) ||
            (this.step === 4 && this.isStep4Complete)) {
            this.setStepCompleted(this.step, true)
            this.setStepCompleted(step, true)
            this.step = step
            this.animating = true
            setTimeout(() => {
              this.animating = false
            }, 500)
          }
        }
      }
    },
    setStepCompleted (step, status) {
      this.$set(this.stepCompleted, 'step' + step, status)
    },
    onValidateUrl () {
      this.partLoading = true
      let urlMatch = this.checkUrlMatch(this.model.validationUrl, this.matchOption)
      let exclude = this.model.targetScope === SURVEY_CONSTANT.TARGET_SCOPE_EXCLUDE

      if (urlMatch === null || (urlMatch && exclude) || (!urlMatch && !exclude)) {
        this.partLoading = false
        this.toggleAlert('notTarget')
        return
      }

      if (!isSameDomain(this.model.validationUrl, 'http://' + this.domain.domain)) {
        this.partLoading = false
        this.toggleAlert('diffDomainUrl')
        return
      }

      checkInstall({
        url: this.model.validationUrl,
        sid: 's' + this.domain.sid
      }, true).then(result => {
        this.partLoading = false
        this.domainValidated = true
        this.toggleAlert('validated')
      }).catch(e => {
        checkInRanking({
          url: this.model.validationUrl,
          sid: this.domain.sid
        }, true).then(result => {
          this.partLoading = false
          if (result.urlFound) {
            this.toggleAlert('warnInstall')
          } else {
            this.toggleAlert('noInstall')
          }
          this.domainValidated = true
        })
      })
    },
    checkUrlMatch (validationUrl, option) {
      try {
        let domain = (option.targetScope === 'all' ? null : this.model.targetUrl) || this.domain.domain || this.domain.realUrl
        if (domain.indexOf('http://') !== 0 && domain.indexOf('https://') !== 0) {
          domain = 'https://' + domain
        }
        if (!isSameDomain(validationUrl, domain)) {
          return null
        }

        if (option.targetScope === SURVEY_CONSTANT.TARGET_SCOPE_ALL) {
          return true
        }

        let valUrl = document.createElement('a')
        valUrl.href = validationUrl
        let tarUrl = document.createElement('a')
        tarUrl.href = option.targetUrl

        if (option.targetMatchType === 'exact') {
          return option.targetUrl === validationUrl
        } else if (option.targetMatchType === 'start') {
          return validationUrl.indexOf(option.targetUrl) === 0
        } else if (option.targetMatchType === 'end' && option.targetMatchString) {
          const revUrl = validationUrl.split('').reverse().join('')
          const revMatchString = option.targetMatchString.split('').reverse().join('')
          return revUrl.indexOf(revMatchString) === 0
        } else if (option.targetMatchType === 'contains' && option.targetMatchString) {
          return validationUrl.indexOf(option.targetMatchString) > -1
        } else if (option.targetMatchType === 'regexp' && option.targetMatchString) {
          const reg = new RegExp(option.targetMatchString)
          return !!reg.test(validationUrl)
        } else if (option.targetMatchType === 'simple') {
          let result = tarUrl.pathname === valUrl.pathname
          if (!result) {
            return false
          }

          if (option.campaignParams) {
            for (const utmOption of this.utmParameters) {
              const utm = utmOption.value

              if (option.campaignParams[utm]) {
                const utmVal = option.campaignParams[utm].value || ''
                const utmMatch = option.campaignParams[utm].matchType || ''
                const utmParam = urlSearchParam(validationUrl, 'utm_' + utm) || ''
                if (utmMatch === 'exact') {
                  if (utmParam !== utmVal) {
                    return false
                  }
                } else if (utmMatch === 'contains') {
                  if (utmParam.indexOf(utmVal) < 0) {
                    return false
                  }
                } else if (utmMatch === 'regexp') {
                  const reg = new RegExp(utmVal)
                  if (!reg.test(utmParam)) {
                    return false
                  }
                } else {
                  return false
                }
              }
            }
          }

          return result
        }
      } catch (e) {
        return false
      }
    },
    setModel (data, duplicating = false, temp = false) {
      this.onInput('surveyName', data.surveyName)
      this.onInput('surveyIntro', data.surveyIntro)
      this.onInput('surveyClosing', data.surveyClosing)
      this.onInput('privacyAgrmt', data.privacyAgrmt)

      let questions = []
      for (const question of Object.values(data.questions || [])) {
        let qModel = {}
        if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RADIO || question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX) {
          qModel = {
            ...this.blankModel.multiple,
            require: !!question.require,
            question: question.question,
            allowMultiple: question.questionType === SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX,
            answerOptions: Object.values(question.answerOptions),
            answerOther: question.answerOther,
            questionId: duplicating ? null : question.questionId,
            questionOrder: duplicating ? 0 : question.questionOrder,
            deleted: question.deleted
          }
        } else if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_TEXT) {
          qModel = {
            ...this.blankModel.essay,
            require: !!question.require,
            question: question.question,
            questionId: duplicating ? null : question.questionId,
            questionOrder: duplicating ? 0 : question.questionOrder,
            deleted: question.deleted
          }
        } else if (question.questionType === SURVEY_CONSTANT.ITEM_TYPE_RANGE) {
          qModel = {
            ...this.blankModel.preference,
            require: !!question.require,
            question: question.question,
            min: {
              description: question.answerOptions.minText,
              value: question.answerOptions.minValue
            },
            max: {
              description: question.answerOptions.maxText,
              value: question.answerOptions.maxValue
            },
            questionId: duplicating ? null : question.questionId,
            questionOrder: duplicating ? 0 : question.questionOrder,
            deleted: question.deleted
          }
        }
        questions.push(qModel)
      }
      questions.sort((a, b) => a.questionOrder - b.questionOrder)
      this.onInput('questions', questions)

      this.onInput('targetDevices', data.targetDevices)
      this.onInput('targetScope', data.targetScope)
      this.onInput('popupLoading', data.popupLoading || SURVEY_CONSTANT.LOAD_AFTER_ONLOAD)
      this.onInput('targetMatchType', data.targetMatchType || 'exact')
      this.onInput('targetUrl', data.targetUrl || '')
      this.onInput('targetMatchString', data.targetMatchString || '')
      this.onInput('customer', 'all')

      if (data.campaignParams) {
        setTimeout(() => {
          this.onInput('customer', 'campaign')
          for (const [key, param] of Object.entries(data.campaignParams)) {
            if (param) {
              this.onInputUtm(key, 'enable', true)
              this.onInputUtm(key, 'value', param.value)
              this.onInputUtm(key, 'matchType', param.matchType)
            }
          }
        }, 0)
      }

      this.onInput('popupStyle', data.popupStyle)
      this.onInput('popupLocation', data.popupLocation)
      this.onInput('lang', data.lang)
      this.onInput('popupColor', data.popupColor)
      this.onInput('timezone', this.domain.timezoneName)
      if (!duplicating && !temp) {
        if (data.tempStart) {
          this.onInput('start', data.tempStart)
        } else {
          const tempStartDate = utcToLocalTimezone(parse(data.startDate), this.domain.timezoneName)
          this.onInput('start', format(isValid(tempStartDate) ? tempStartDate : tempStartDate, 'YYYY-MM-DD'))
        }
        if (data.tempEnd) {
          this.onInput('end', data.tempEnd)
        } else {
          const tempEndDate = utcToLocalTimezone(parse(data.endDate), this.domain.timezoneName)
          this.onInput('end', format(isValid(tempEndDate) ? tempEndDate : tempEndDate, 'YYYY-MM-DD'))
        }
      }

      setTimeout(() => {
        this.valueChanged = false
      }, 1000)
    },
    onPreview (tempSaved = false) {
      const agent = navigator.userAgent.toLowerCase()
      if ((navigator.appName === 'Netscape' && agent.indexOf('trident') !== -1) || (agent.indexOf('msie') !== -1)) {
        this.toggleAlert('noPreviewForIe')
        return
      }

      if (!this.isDraft && this.survey.surveyId) {
        if (tempSaved === true) {
          this.previewUrl = ''
          this.previewUrlError = ''
          if (this.model.targetScope === 'all') {
            this.previewUrl = 'http://' + this.domain.domain
          } else if (this.model.targetScope === 'include') {
            if (['exact', 'simple', 'start'].includes(this.model.targetMatchType)) {
              this.previewUrl = this.model.targetUrl
            }
          }
          this.previewModal = true
        } else {
          this.partLoading = true
          this.onConfirm(true).then(() => {
            this.partLoading = false
            this.onPreview(true)
          })
        }
      } else {
        if (this.valueChanged || !this.survey.tempSurveyId) {
          this.toggleAlert('saveBeforePreview')
        } else {
          this.previewUrl = ''
          this.previewUrlError = ''
          if (this.model.targetScope === 'all') {
            this.previewUrl = 'http://' + this.domain.domain
          } else if (this.model.targetScope === 'include') {
            if (['exact', 'simple', 'start'].includes(this.model.targetMatchType)) {
              this.previewUrl = this.model.targetUrl
            }
          }
          this.previewModal = true
        }
      }
    },
    onConfirmPreview () {
      if (!this.previewUrl) {
        this.previewUrlError = 'requiredUrl'
        return
      }
      if ((this.previewUrl.indexOf('http://') !== 0 && this.previewUrl.indexOf('https://') !== 0) || this.previewUrl.indexOf('.') === -1) {
        this.previewUrlError = 'diffFormatUrl'
        return
      }

      if (!isSameDomain(this.previewUrl, 'http://' + this.domain.domain)) {
        this.previewUrlError = 'diffDomainUrl'
        return
      }

      let url = this.previewUrl
      url += `${url.indexOf('?') === -1 ? '?' : '&'}_ba_survey_preview=${this.survey.tempSurveyId}`
      url += `&_ba_usernum=${this.userData.userNum}`

      window.open(url, isInIframe() ? '_self' : '_blank')
      this.previewModal = false
    },
    onSaveBeforePreview () {
      this.toggleAlert('saveBeforePreview')
      this.onConfirm(true).then(() => {
        this.onPreview()
      })
    },
    onConfirm (preview = false) {
      preview = preview === true
      if (!preview) {
        EventBus.$emit('onLoading', true)
      }

      this.registering = true
      let model = {
        sid: this.sid,
        surveyName: this.model.surveyName,
        surveyIntro: this.model.surveyIntro,
        privacyAgrmt: this.model.privacyAgrmt,
        surveyClosing: this.model.surveyClosing,
        questions: [],
        targetDevices: this.model.targetDevices,
        popupLoading: this.model.popupLoading,
        popupStyle: this.model.popupStyle,
        popupLocation: this.model.popupStyle === 'popup' ? this.model.popupLocation : null,
        lang: this.model.lang,
        popupColor: this.model.popupColor,
        timezone: this.domain.timezoneName,
        startDate: this.model.start,
        endDate: this.model.end,
        ...this.matchOption
      }

      if (this.isEditing) {
        if (this.survey.surveyId) {
          model.surveyId = this.survey.surveyId
        }
        if (this.survey.tempSurveyId) {
          model.tempSurveyId = this.survey.tempSurveyId
        }
      }

      // let today = this.today
      let today = format(utcToLocalTimezone(new Date(), this.domain.timezoneName), 'YYYY-MM-DD')
      if (model.startDate === 'now') {
        model.startDate = today
      }
      today = utcToLocalTimezone(new Date(), this.domain.timezoneName)
      if (model.endDate === 'after14') {
        model.endDate = addDays(parse(model.startDate), 14)
      } else if (model.endDate === 'after30') {
        model.endDate = addDays(parse(model.startDate), 30)
      }
      model.endDate = format(model.endDate, 'YYYY-MM-DD')

      let q = 0

      for (const question of this.model.questions) {
        let qModel = {
          questionType: null,
          require: question.require,
          question: question.question,
          questionOrder: question.deleted ? null : q,
          answerOptions: null,
          answerOtherText: null,
          answerOther: false,
          deleted: question.deleted || false
        }

        if (question.questionType === 'multiple') {
          if (question.allowMultiple) {
            qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX
          } else {
            qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_RADIO
          }
          qModel.answerOptions = {}
          let o = 0
          for (const option of question.answerOptions) {
            qModel.answerOptions[o] = option
            o++
          }
          qModel.answerOther = question.answerOther
        } else if (question.questionType === 'essay') {
          qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_TEXT
        } else if (question.questionType === 'preference') {
          qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_RANGE
          qModel.answerOptions = {
            maxValue: question.max.value,
            minValue: question.min.value,
            maxText: question.max.description,
            minText: question.min.description
          }
        }
        if (question.questionId !== null) {
          qModel.questionId = question.questionId
        }
        model.questions.push(qModel)
        if (!question.deleted) {
          q++
        }
      }

      for (const question of this.deletedQuestionsList) {
        let qModel = {
          questionType: null,
          require: question.require,
          question: question.question,
          questionOrder: question.deleted ? null : q,
          answerOptions: null,
          answerOtherText: null,
          answerOther: false,
          deleted: question.deleted || false
        }

        if (question.questionType === 'multiple') {
          if (question.allowMultiple) {
            qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_CHECKBOX
          } else {
            qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_RADIO
          }
          qModel.answerOptions = {}
          let o = 0
          for (const option of question.answerOptions) {
            qModel.answerOptions[o] = option
            o++
          }
          qModel.answerOther = question.answerOther
        } else if (question.questionType === 'essay') {
          qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_TEXT
        } else if (question.questionType === 'preference') {
          qModel.questionType = SURVEY_CONSTANT.ITEM_TYPE_RANGE
          qModel.answerOptions = {
            maxValue: question.max.value,
            minValue: question.min.value,
            maxText: question.max.description,
            minText: question.min.description
          }
        }
        if (question.questionId !== null) {
          qModel.questionId = question.questionId
        }
        model.questions.push(qModel)
        if (!question.deleted) {
          q++
        }
      }

      const questions = model.questions
      const deletedQuestions = this.deletedQuestionsList
      model.questions = questions.concat(deletedQuestions)

      if (preview) {
        model.isTemp = true
        model.stepCompleted = this.stepCompleted
        model.tempStart = this.model.start
        model.tempEnd = this.model.end
        if (model.tempSurveyId) {
          this.$set(this.survey, 'status', SURVEY_CONSTANT.STATUS_PREVIEW)
          return new Promise(resolve => {
            updateSurvey(model, true).then(result => {
              this.saveDraft = true
              this.valueChanged = false
              setTimeout(() => {
                this.saveDraft = false
              }, 1500)
              const sid = this.domain.orgSid
              let newSurvey = {
                ...result.survey,
                tempSurveyId: model.tempSurveyId || this.$route.query.tempSurveyId,
                questions: result.questions
              }
              let newList = [...this.surveyList[sid]]
              const surveyIndex = newList.findIndex(el => (!!el && el.tempSurveyId === newSurvey.tempSurveyId))
              if (surveyIndex !== -1) {
                newList[surveyIndex] = {
                  ...this.surveyDetail[`${sid}-temp-${newSurvey.tempSurveyId}`],
                  ...newList[surveyIndex],
                  ...newSurvey
                }
              }
              // delete newSurvey.tempSurveyId
              this.setSurveyList([sid, newList])
              this.setSurveyDetail([`${sid}-temp-${newSurvey.tempSurveyId}`, newSurvey])
              resolve()
            })
          })
        } else {
          model.start = model.startDate
          model.end = model.endDate
          if (model.surveyId) {
            model.linkSurveyId = model.surveyId
          } else {
            model.stepCompleted = this.stepCompleted
          }

          return new Promise(resolve => {
            createSurvey({
              ...model,
              surveyId: null
            }, true).then(result => {
              this.$set(this.survey, 'tempSurveyId', result.tempSurveyId)
              if (!model.surveyId) {
                this.$router.push({ name: this.$route.name, query: { sid: this.$route.query.sid, tempSurveyId: result.tempSurveyId } })
              }
              this.saveDraft = true
              this.valueChanged = false
              setTimeout(() => {
                this.saveDraft = false
              }, 1500)
              if (this.isDraft) {
                const sid = this.domain.orgSid
                let newSurvey = {
                  ...model,
                  ...result,
                  status: SURVEY_CONSTANT.STATUS_PREVIEW,
                  regDate: format(utcToLocalTimezone(new Date(), this.domain.timezoneName), 'YYYY-MM-DD HH:mm:ss')
                }
                let newList = [...this.surveyList[sid]]
                newList.push(newSurvey)
                this.setSurveyList([sid, newList])
                this.setSurveyDetail([`${sid}-temp-${newSurvey.tempSurveyId}`, newSurvey])
              }
              resolve()
            })
          })
        }
      } else {
        if (model.surveyId) {
          return new Promise(resolve => {
            updateSurvey(model, true).then(result => {
              this.valueChanged = false
              setTimeout(() => {
                this.$router.push({ name: 'survey', query: { sid: this.$route.query.sid, survey: result.survey.surveyId } })
              }, 300)
              const sid = this.domain.orgSid
              let newSurvey = {
                ...this.surveyDetail[`${sid}-${result.survey.surveyId}`],
                ...result.survey,
                questions: result.questions
              }
              let newList = [...this.surveyList[sid]]
              const surveyIndex = newList.findIndex(el => el.surveyId && el.surveyId === newSurvey.surveyId)
              if (surveyIndex !== -1) {
                newList[surveyIndex] = newSurvey
              }
              if (newSurvey.tempSurveyId) {
                delete newSurvey.tempSurveyId
              }
              this.setSurveyList([sid, newList])
              this.setSurveyDetail([`${sid}-${newSurvey.surveyId}`, newSurvey])
              resolve()
            })
          })
        } else {
          model.start = model.startDate
          model.end = model.endDate
          model.stepCompleted = this.stepCompleted
          return new Promise(resolve => {
            createSurvey(model, true).then(result => {
              if (result.runningCount) {
                EventBus.$emit('onLoading', false)
                this.isRunningSurvey = true
              } else {
                this.valueChanged = false
                setTimeout(() => {
                  this.$router.push({ name: 'survey', query: { sid: this.$route.query.sid, survey: result.surveyId } })
                }, 300)
                const sid = this.domain.orgSid
                let newSurvey = {
                  ...model,
                  ...result,
                  answerCount: 0,
                  status: SURVEY_CONSTANT.STATUS_WAITING,
                  regDate: format(utcToLocalTimezone(new Date(), this.domain.timezoneName), 'YYYY-MM-DD HH:mm:ss')
                }
                let newList = [...this.surveyList[sid]]
                if (model.tempSurveyId) {
                  for (let i = 0; i < newList.length; i++) {
                    if (newList[i] &&
                      (newList[i].tempSurveyId === model.tempSurveyId)) {
                      delete newList[i]
                    }
                  }
                }
                newList.push(newSurvey)
                this.setSurveyList([sid, newList])
                this.setSurveyDetail([`${this.domain.orgSid}-${newSurvey.surveyId}`, newSurvey])
                resolve()
              }
            })
          })
        }
      }
    },
    onInput (model, value) {
      this.valueChanged = true
      if (model === 'surveyClosing') {
        this.surveyClosingTouched = true
      }
      this.$set(this.model, model, value)
      if (model === 'popupStyle' && value === SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS) {
        this.onInput('popupLocation', SURVEY_CONSTANT.POPUP_LOC_RIGHT)
      } else if (model === 'validationUrl') {
        this.domainValidated = false
      }
    },
    onInputQuestion (index, model, value) {
      this.valueChanged = true
      this.$set(this.model.questions[index], model, value)
    },
    onInputQuestionPreference (index, target, model, value) {
      this.valueChanged = true
      this.$set(this.model.questions[index][target], model, value)
    },
    onInputQuestionOption (index, i, value) {
      this.valueChanged = true
      this.$set(this.model.questions[index].answerOptions, i, value)
    },
    onInputQuestionOptionDelete (index, i) {
      let question = this.model.questions[index]

      let surveyId = Number(this.$route.query.survey)

      if (surveyId) {
        const checkPayload = {
          sid: this.$route.query.sid,
          surveyId: surveyId,
          questionId: question.questionId
        }
        getQuestionCountByDelete(checkPayload, true).then(result => {
          const answerCount = result.answerCount
          if (answerCount) {
            this.deletingQuestion = index
            this.deletingOption = i
            this.toggleAlert('deletingOption')
          } else {
            this.onConfirmQuestionOptionDelete(index, i)
          }
        })
      } else {
        this.onConfirmQuestionOptionDelete(index, i)
      }
    },
    onConfirmQuestionOptionDelete (index, i) {
      this.$set(this.alert, 'deletingOption', false)
      this.valueChanged = true
      let model = this.model.questions[index].answerOptions
      if (model.length > 1) {
        model.splice(i, 1)
        this.$set(this.model.questions[index], 'answerOptions', [...model])
      }
    },
    onInputQuestionOptionAdd (index) {
      this.valueChanged = true
      let model = this.model.questions[index].answerOptions
      model.push('')
      this.$set(this.model.questions[index], 'answerOptions', model)
      setTimeout(() => {
        document.getElementById(`option-${index}-${model.length - 1}`).focus()
      }, 30)
    },
    onChangeQuestionType (index, value) {
      this.valueChanged = true
      let model = this.model.questions

      const blankModel = this.blankModel[value]
      if (value === 'preference') {
        blankModel.min = {
          description: '',
          value: 1
        }
        blankModel.max = {
          description: '',
          value: 5
        }
      }
      if (value === 'multiple') {
        blankModel.answerOptions = ['']
      }
      model.splice(index, 1, {
        ...blankModel,
        question: model[index].question,
        require: model[index].require
      })
      this.$set(this.model, 'questions', [...model])
    },
    onShowQuestionType (index, value) {
      this.$set(this.showQuestionType, index, value)
    },
    onHideQuestionType (index) {
      this.$set(this.showQuestionType, index, null)
    },
    addQuestion (focus = true) {
      this.valueChanged = true
      this.$set(this.model, 'questions', [
        ...this.model.questions,
        { ...this.blankModel.multiple, answerOptions: [''] }
      ])

      if (focus) {
        setTimeout(() => {
          document.getElementById(`question-${this.computedQuestions.length - 1}`).focus()
        }, 30)
      }
    },
    duplicateQuestion (index) {
      this.valueChanged = true
      let model = this.model.questions
      let newQuestion = { ...model[index] }
      if (newQuestion.answerOptions && Array.isArray(newQuestion.answerOptions)) {
        newQuestion.answerOptions = [...newQuestion.answerOptions]
      }
      if (newQuestion.min) {
        newQuestion.min = { ...newQuestion.min }
      }
      if (newQuestion.max) {
        newQuestion.max = { ...newQuestion.max }
      }
      newQuestion.questionId = null
      model.splice(index, 0, newQuestion)
      this.$set(this.model, 'questions', [...model])
    },
    deleteQuestion (index) {
      let question = this.model.questions[index]
      let surveyId = Number(this.$route.query.survey)

      if (surveyId) {
        const checkPayload = {
          sid: this.$route.query.sid,
          surveyId: surveyId,
          questionId: question.questionId
        }
        getQuestionCountByDelete(checkPayload, true).then(result => {
          const answerCount = result.answerCount
          if (answerCount) {
            this.deletingQuestion = index
            this.toggleAlert('deletingQuestion')
          } else {
            this.confirmDeleteQuestion(index)
          }
        })
      } else {
        this.confirmDeleteQuestion(index)
      }
      // if (this.hasAnswer[question.questionId]) {
      //   this.deletingQuestion = index
      //   this.toggleAlert('deletingQuestion')
      // } else {
      //   this.confirmDeleteQuestion(index)
      // }
    },
    confirmDeleteQuestion (index) {
      this.valueChanged = true
      this.$set(this.alert, 'deletingQuestion', false)
      let model = this.model.questions
      if (model.length > 1) {
        model[index].deleted = true
        if (this.isEditing) {
          this.deletedQuestionsList.push(model[index])
        }
        this.$set(this.model, 'questions', this.model.questions.filter(item => {
          return !item.deleted
        }))
      }
    },
    toggleDevice (device) {
      this.valueChanged = true
      let model = this.model.targetDevices
      if (model.includes(device)) {
        if (model.length > 1) {
          model = model.filter((el) => el !== device)
        }
      } else {
        model.push(device)
      }
      this.onInput('targetDevices', model)
    },
    onInputUtm (parameter, model, value) {
      this.valueChanged = true
      this.$set(this.model.campaignParams[parameter], model, value)
    },
    togglePreviewLayer () {
      this.previewToggle = !this.previewToggle
      if (this.model.popupStyle === 'modal') {
        setTimeout(() => {
          this.previewToggle = true
        }, 5000)
      }
    },
    toggleAlert (key) {
      this.$set(this.alert, key, !this.alert[key])
    },
    onInputPreview (value) {
      this.previewUrlError = ''
      this.previewUrl = value
    },
    calcHasRunning () {
      this.hasRunning = false
      for (const survey of this.surveys) {
        if ([SURVEY_CONSTANT.STATUS_RUNNING, SURVEY_CONSTANT.STATUS_WAITING].includes(survey.status)) {
          this.hasRunning = true
          break
        }
      }
    },
    onScroll (e) {
      this.scrollTop = e.target.scrollTop
    },
    onTempData (confirm) {
      if (confirm) {
        this.afterLoad(this.tempData)
      } else {
        this.useTemp = false
        this.partLoading = true
        deleteSurvey({
          sid: this.$route.query.sid,
          isTemp: true,
          linkSurveyId: this.$route.query.survey
        }, true).then(() => {
          this.partLoading = false
          this.load()
        })
      }
      this.toggleAlert('checkTempData')
    }
  },
  watch: {
    '$i18n.locale' () {
      if (!this.surveyClosingTouched) {
        this.model.surveyClosing = this.$t('userResearch.survey.create.initial.surveyClosing')
      }
    },
    calendarStartDate (value, old) {
      if (this.model.end.includes('-')) {
        if (!this.loading) {
          const nextDay = format(addDays(parse(value), 1), 'YYYY-MM-DD')
          if (this.calendarEndDate < nextDay) {
            this.onInput('end', nextDay)
          }
        }
      }
      if (old) {
        setTimeout(() => {
          this.onInput('start', value)
        }, 300)
      }
    },
    calendarEndDate (value, old) {
      if (!this.loading) {
        if (old) {
          setTimeout(() => {
            this.onInput('end', value)
          }, 300)
        }
      }
    },
    'model.start' (value) {
      if (value.includes('-')) {
        setTimeout(() => {
          this.calendarStartDate = value
        }, this.loading ? 0 : 300)
      }
    },
    'model.end' (value) {
      if (value.includes('-')) {
        const nextDay = format(addDays(parse(this.calendarStartDate), 1), 'YYYY-MM-DD')
        if (!value && value < nextDay) {
          this.onInput('end', nextDay)
        } else {
          this.calendarEndDate = value
          setTimeout(() => {
            this.calendarEndDate = value
          }, this.loading ? 0 : 300)
        }
      }
    },
    'model.targetScope' (value) {
      this.onInput('customer', 'all')
    },
    'model.targetMatchType' (value, old) {
      this.onInput('customer', 'all')
      if (old) {
        this.model.targetMatchString = ''
        this.model.targetUrl = ''
      }
    },
    'model.customer' (value, old) {
      if (!old || value !== 'campaign') {
        this.utmParameters.map((v) => {
          this.model.campaignParams[v.value].value = ''
          this.model.campaignParams[v.value].enable = false
          this.model.campaignParams[v.value].matchType = 'exact'
        })
      }
    }
  },
  computed: {
    ...mapState([
      'userData',
      'surveyList',
      'surveyDetail'
    ]),
    maxStep1Height () {
      let height = 1135

      for (const question of this.computedQuestions) {
        height += 320
        if (question.questionType === 'multiple') {
          height += (question.answerOptions.length + 1) * 75
        }
      }

      return height
    },
    isEditing () {
      return !!this.survey.surveyId || !!this.survey.tempSurveyId
    },
    isDraft () {
      return !this.survey.surveyId && !!this.$route.query.tempSurveyId
    },
    isStep1QuestionComplete () {
      for (const question of this.model.questions) {
        if (!question.deleted) {
          if (!question.question || question.question.length > 1000) {
            return false
          }
          if (Array.isArray(question.answerOptions)) {
            for (const option of question.answerOptions) {
              if (!option || option.length > 100) {
                return false
              }
            }
          }
          if (question.max) {
            if (question.max.description.length > 100) {
              return false
            }
          }
          if (question.min) {
            if (question.min.description.length > 100) {
              return false
            }
          }
        }
      }
      return true
    },
    isStep1Complete () {
      return this.model.surveyName && this.model.surveyName.length <= 1000 && this.model.surveyIntro.length <= 1000 && this.computedQuestions.length && this.model.surveyClosing && this.model.surveyClosing.length <= 1000 && this.isStep1QuestionComplete
    },
    isStep2DeviceComplete () {
      return this.model.targetDevices.length > 0
    },
    isStep2UtmComplete () {
      return this.model.customer !== 'campaign' ||
        !(!this.model.campaignParams.source.enable && !this.model.campaignParams.campaign.enable && !this.model.campaignParams.medium.enable && !this.model.campaignParams.content.enable && !this.model.campaignParams.term.enable)
    },
    isStep2UtmValueComplete () {
      return (!this.model.campaignParams.source.enable || this.model.campaignParams.source.value) && (!this.model.campaignParams.campaign.enable || this.model.campaignParams.campaign.value) &&
        (!this.model.campaignParams.medium.enable || this.model.campaignParams.medium.value) && (!this.model.campaignParams.content.enable || this.model.campaignParams.content.value) &&
        (!this.model.campaignParams.term.enable || this.model.campaignParams.term.value)
    },
    isStep2TargetUrlComplete () {
      if (this.model.targetScope === 'all') {
        return true
      }

      if ((this.model.targetUrl.indexOf('http://') !== 0 && this.model.targetUrl.indexOf('https://') !== 0) || this.model.targetUrl.indexOf('.') === -1) {
        return false
      }

      if (!isSameDomain(this.model.targetUrl, 'http://' + this.domain.domain)) {
        return null
      }

      return true
    },
    isStep2Complete () {
      return this.isStep2UtmComplete && this.isStep2DeviceComplete && this.isStep2UtmValueComplete && this.isStep2TargetUrlComplete
    },
    isStep3Complete () {
      return true
    },
    isStep4Complete () {
      return true
    },
    isAllComplete () {
      return this.isStep1Complete && this.isStep2Complete && this.isStep3Complete && this.isStep4Complete &&
        this.stepCompleted.step1 === true && this.stepCompleted.step2 === true && this.stepCompleted.step3 === true && this.stepCompleted.step4 === true
    },
    requireNoRunning () {
      return (this.hasRunning && !(this.isEditing && !this.isDraft))
    },
    customerOptions () {
      let options = [
        { value: 'all', text: this.$t('userResearch.survey.create.customer.all') }
      ]
      if (this.model.targetMatchType === 'simple') {
        options.push({ value: 'campaign', text: this.$t('userResearch.survey.create.customer.campaign') })
      }
      return options
    },
    questionTypes () {
      return [
        { value: 'multiple', text: this.$t('userResearch.survey.questionType.multiple') },
        { value: 'essay', text: this.$t('userResearch.survey.questionType.essay') },
        { value: 'preference', text: this.$t('userResearch.survey.questionType.preference') }
      ]
    },
    deviceOptions () {
      return [
        { value: 'desktop', text: this.$t('common.deviceType.desktop2') },
        { value: 'tablet', text: this.$t('common.deviceType.tablet2') },
        { value: 'phone', text: this.$t('common.deviceType.phone2') }
      ]
    },
    targetScopeOptions () {
      return [
        { value: SURVEY_CONSTANT.TARGET_SCOPE_ALL, text: this.$t('userResearch.survey.create.targetScope.all') },
        { value: SURVEY_CONSTANT.TARGET_SCOPE_INCLUDE, text: this.$t('userResearch.survey.create.targetScope.include') },
        { value: SURVEY_CONSTANT.TARGET_SCOPE_EXCLUDE, text: this.$t('userResearch.survey.create.targetScope.exclude') }
      ]
    },
    popupLoadingOptions () {
      return [
        { value: SURVEY_CONSTANT.LOAD_AFTER_ONLOAD, text: this.$t('userResearch.survey.create.popupLoading.afterLoad') },
        { value: SURVEY_CONSTANT.LOAD_AFTER_SCROLL, text: this.$t('userResearch.survey.create.popupLoading.scrollToHalf') },
        { value: SURVEY_CONSTANT.LOAD_AFTER_5MIN, text: this.$t('userResearch.survey.create.popupLoading.after5min') },
        { value: SURVEY_CONSTANT.LOAD_AFTER_CONVERT, text: this.$t('userResearch.survey.create.popupLoading.afterConvert') }
      ]
    },
    matchTypeOptions () {
      return [
        { value: 'exact', text: this.$t('common.matchType.exact') },
        { value: 'start', text: this.$t('common.matchType.start') },
        { value: 'end', text: this.$t('common.matchType.end') },
        { value: 'contains', text: this.$t('common.matchType.contain') },
        { value: 'regexp', text: this.$t('common.matchType.regexp') },
        { value: 'simple', text: this.$t('common.matchType.simple') }
      ]
    },
    matchOptions () {
      return {
        exact: { matchText: '', matchTextPlaceholder: '' },
        start: { matchText: '', matchTextPlaceholder: '' },
        end: { matchText: this.$t('userResearch.survey.create.matchText.end'), matchTextPlaceholder: '/signup' },
        contains: { matchText: this.$t('userResearch.survey.create.matchText.contain'), matchTextPlaceholder: '?x=1' },
        regexp: { matchText: this.$t('userResearch.survey.create.matchText.regexp'), matchTextPlaceholder: '\\/pages\\/[^\\/]+' },
        simple: { matchText: '', matchTextPlaceholder: '' }
      }
    },
    utmParameters () {
      return [
        { value: 'source', text: this.$t('journey.targeting.utm.source') },
        { value: 'campaign', text: this.$t('journey.targeting.utm.campaign') },
        { value: 'medium', text: this.$t('journey.targeting.utm.medium') },
        { value: 'content', text: this.$t('journey.targeting.utm.content') },
        { value: 'term', text: this.$t('journey.targeting.utm.term') }
      ]
    },
    utmConditionOptions () {
      return [
        { value: 'exact', text: this.$t('userResearch.survey.create.utmCondition.exact') },
        { value: 'contains', text: this.$t('userResearch.survey.create.utmCondition.contains') },
        { value: 'regexp', text: this.$t('userResearch.survey.create.utmCondition.regexp') }
      ]
    },
    popoverOptions () {
      return [
        { value: SURVEY_CONSTANT.POPUP_LOC_RIGHT, text: this.$t('common.right') },
        { value: SURVEY_CONSTANT.POPUP_LOC_LEFT, text: this.$t('common.left') }
      ]
    },
    sid () {
      return this.$route.query.sid
    },
    matchOption () {
      let model = {
        targetScope: this.model.targetScope,
        targetUrl: this.model.targetScope === 'all' ? null : this.model.targetUrl,
        targetMatchType: this.model.targetScope === 'all' ? null : this.model.targetMatchType,
        targetMatchString: this.model.targetScope === 'all' ? null : this.model.targetMatchString,
        campaignParams: null
      }

      if (this.model.targetScope !== 'all' && this.model.customer === 'campaign') {
        let orgParams = this.model.campaignParams
        model.campaignParams = {
          source: orgParams.source.enable ? { value: orgParams.source.value, matchType: orgParams.source.matchType } : null,
          campaign: orgParams.campaign.enable ? { value: orgParams.campaign.value, matchType: orgParams.campaign.matchType } : null,
          medium: orgParams.medium.enable ? { value: orgParams.medium.value, matchType: orgParams.medium.matchType } : null,
          content: orgParams.content.enable ? { value: orgParams.content.value, matchType: orgParams.content.matchType } : null,
          term: orgParams.term.enable ? { value: orgParams.term.value, matchType: orgParams.term.matchType } : null
        }
      }

      return model
    },
    computedQuestions () {
      if (this.model) {
        return this.model.questions.map((el, index) => { return { ...el, index } }).filter(el => !el.deleted)
      }
      return []
    },
    previewingLanguage () {
      const returnLangValue = {
        'ko': 0,
        'ja': 1,
        'en': 2
      }

      let lang = 'ko'

      if (this.model.lang === '') {
        lang = navigator.language.split('_')[0]

        if ([SURVEY_CONSTANT.LANG_KO, SURVEY_CONSTANT.LANG_EN, SURVEY_CONSTANT.LANG_JA].indexOf(lang) < 0) {
          lang = SURVEY_CONSTANT.LANG_EN
        }
      } else {
        lang = this.model.lang
      }

      return returnLangValue[lang]
    },
    startMinDate () {
      return addDays(utcToLocalTimezone(new Date(), this.domain.timezoneName), 1)
    },
    endMinDate () {
      if (this.isEditing && this.survey.status === SURVEY_CONSTANT.STATUS_RUNNING) {
        return utcToLocalTimezone(new Date(), this.domain.timezoneName)
      } else {
        return parse(this.calendarStartDate)
      }
    }
  },
  data () {
    const today = format(new Date(), 'YYYY-MM-DD')
    return {
      SURVEY_CONSTANT: SURVEY_CONSTANT,
      isRunningSurvey: false,
      loading: true,
      partLoading: false,
      useTemp: true,
      tempData: null,

      step: 0,

      today: today,
      calendarStartDate: '', // format(addDays(utcToLocalTimezone(new Date(), this.domain.timezoneName), 1), 'YYYY-MM-DD'),
      calendarEndDate: '', // format(addDays(utcToLocalTimezone(new Date(), this.domain.timezoneName), 2), 'YYYY-MM-DD'),

      domain: {},
      surveys: {},
      survey: {},

      hasAnswer: {},
      domainValidated: false,

      isSurveyPrivacy: false,
      isSurveyPrivacyExample: false,

      deletedQuestionsList: [],
      surveyClosingTouched: false,

      model: {
        surveyName: '',
        surveyIntro: '',
        privacyAgrmt: '',
        surveyClosing: this.$t('userResearch.survey.create.initial.surveyClosing'),
        questions: [],
        targetDevices: ['desktop', 'tablet', 'phone'],
        targetScope: SURVEY_CONSTANT.TARGET_SCOPE_ALL,
        popupLoading: SURVEY_CONSTANT.LOAD_AFTER_ONLOAD,
        targetMatchType: null,
        targetUrl: '',
        targetMatchString: '',
        customer: null,
        campaignParams: {
          source: {
            enable: false,
            value: '',
            matchType: 'exact'
          },
          campaign: {
            enable: false,
            value: '',
            matchType: 'exact'
          },
          medium: {
            enable: false,
            value: '',
            matchType: 'exact'
          },
          content: {
            enable: false,
            value: '',
            matchType: 'exact'
          },
          term: {
            enable: false,
            value: '',
            matchType: 'exact'
          }
        },
        validationUrl: '',
        popupStyle: SURVEY_CONSTANT.POPUP_STYLE_POPUP_ALWAYS,
        popupLocation: SURVEY_CONSTANT.POPUP_LOC_RIGHT,
        lang: this.$i18n.locale,
        popupColor: '#ffffff',
        timezone: 'Asia/Seoul',
        start: 'now',
        end: 'after30'
      },
      modelError: {
        surveyName: '',
        surveyClosing: ''
      },

      showQuestionType: {},

      blankModel: {
        multiple: {
          questionId: null,
          questionType: 'multiple',
          require: false,
          question: '',
          allowMultiple: false,
          answerOptions: [''],
          answerOther: false,
          deleted: false
        },
        essay: {
          questionId: null,
          questionType: 'essay',
          require: false,
          question: '',
          answerOptions: null,
          deleted: false
        },
        preference: {
          questionId: null,
          questionType: 'preference',
          require: false,
          question: '',
          min: {
            description: '',
            value: 1
          },
          max: {
            description: '',
            value: 5
          },
          deleted: false
        }
      },
      languageOptions: [
        { value: SURVEY_CONSTANT.LANG_KO, text: '한국어' },
        { value: SURVEY_CONSTANT.LANG_EN, text: 'English' },
        { value: SURVEY_CONSTANT.LANG_JA, text: '日本語' }
      ],
      colorOptions: [],

      stepCompleted: {
        step1: false,
        step2: false,
        step3: false,
        step4: false
      },
      animating: false,
      registering: false,

      previewing: 'desktop',
      previewToggle: true,
      previewModal: false,
      previewUrl: '',
      previewUrlError: '',
      previewHelp: false,

      dragging: false,
      dragOptions: {
        animation: 200,
        disabled: false,
        ghostClass: 'ghost'
      },

      alert: {
        notTarget: false,
        diffDomainUrl: false,
        warnInstall: false,
        noInstall: false,
        validated: false,
        saveBeforePreview: false,
        checkTempData: false,
        deletingQuestion: false,
        deletingOption: false,
        noPreviewForIe: false
      },

      deletingQuestion: null,
      deletingOption: null,

      saveDraft: false,
      valueChanged: false,
      hasRunning: false,
      scrollTop: 0,

      languageType: SURVEY_CONSTANT.LANG_TYPE_BOWSER
    }
  }
}
</script>

<style lang="scss" scoped>
.add-survey {
  .add-survey-wrap {
    &.fade-enter-active, &.fade-leave-active {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &.fade-enter, &.fade-leave-to{
      opacity: 0;
    }
  }

  .add-header {
    position: relative;
    margin: 20px 0 0;
    height: 231px;
    min-height: 139px !important;
    padding-top: 5px;
    overflow: hidden;

    .ui-btn {
      margin-left: 30px;
      border: none;
      box-shadow: 0 9px 11px -5px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.08);
      font-size: 13px;
      height: 34px;
    }

    .indicator-gap {
      height: 63px;
      min-height: 0 !important;
    }

    .step-indicator {
      position: relative;
      width: 646px;
      height: 2px;
      margin: 0 auto 63px;
      background-color: #d7d7d7;
      overflow: visible;

      .step {
        position: absolute;
        top: -13px;
        height: 0;
        transform: translateX(-50%);
        white-space: nowrap;

        &.step-1 {
          left: 0;
        }

        &.step-2 {
          left: 33.333333%;
        }

        &.step-3 {
          left: 66.666666%;
        }

        &.step-4 {
          left: 100%;
        }

        .bullet {
          display: block;
          position: relative;
          width: 26px;
          height: 26px;
          border: solid 1px #d7d7d7;
          background-color: #f4f4f1;
          border-radius: 20px;
          margin: 0 auto;
          transition: background-color 0.2s ease,
                      border-color 0.2s ease;

          i {
            display: block;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transition: opacity 0.2s ease;

            &.icon {
              z-index: 2;
              top: -10px;
              right: -13px;
              left: auto;
              transform: none;
              opacity: 0;
              width: 14px;
              height: 14px;
              background-color: #57ab00;
              border-radius: 10px;
              padding: 3px;
              background-size: 14px 14px;
            }

            &.number {
              font-size: 14px;
              font-weight: 500;
              color: #d1d1d1;
              transition: color 0.2s ease;
            }
          }
        }

        p {
          position: relative;
          white-space: nowrap;
          font-size: 13px;
          font-weight: 500;
          line-height: 1.23;
          color: #d1d1d1;
          padding-top: 8px;
          transition: color 0.2s ease;
        }

        &.opened {
          cursor: pointer;

          .bullet {
            border-color: #bbb;
            background-color: #fff;

            i.number {
              color: #888;
            }
          }

          p {
            color: #777;
          }
        }

        &.complete {
          .bullet {
            i.icon {
              opacity: 1;
            }
          }
        }

        &.ing {
          cursor: pointer;

          .bullet {
            border-color: #ec0047;
            background-color: #ec0047;

            i.number {
              color: #fff;
            }
          }

          p {
            color: #ec0047;
          }
        }
      }
    }

    .add-notice {
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 970px;
      background-color: #f4e439;
      margin: 0 auto;
      transition: width 0.15s ease;
      color: #2f2f2f;

      .add-notice-wrap {
        display: flex;
        align-items: center;
        column-gap: 4px;
        max-width: 956px;
        padding: 7px;
        margin: 0 auto;
      }

      .icon--ring {
        vertical-align: middle;
      }
    }

    &::after {
      content: "";
      display: block;
      position: absolute;
      bottom: -15px;
      left: 0;
      width: 100%;
      height: 15px;
      background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0));
      border-top: 1px solid #d0d0cd;
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    &.scroll {
      .add-notice {
        width: 100%;
      }

      &::after {
        opacity: 1;
      }
    }

    &.clear {
      min-height: 106px !important;

      .indicator-gap {
        min-height: 3px !important;
      }
    }

    &.editing {
      min-height: 50px !important;
      height: 50px !important;
    }
  }

  .add-wrap {
    height: calc(100vh - 309px);
    max-height: calc(100vh - 217px) !important;
    overflow: auto;

    &.clear {
      max-height: calc(100vh - 189px) !important;
    }

    &.editing {
      max-height: calc(100vh - 133px) !important;
      height: calc(100vh - 133px) !important;

      .add-body {
        margin-top: -10px;
      }
    }
  }

  .add-body {
    margin: 0 20px;

    .step {
      width: 970px;
      overflow: hidden;
      border-radius: 14px;
      box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.08);
      background-color: #fff;
      margin: 20px auto;
      transition: max-height 0.5s cubic-bezier(0.23, 1, 0.32, 1);

      .step-header {
        position: relative;
        padding: 16px 20px;
        opacity: 0.3;
        transition: opacity 0.2s ease, padding 0.2s ease;

        .arrow {
          position: absolute;
          top: 20px;
          right: 20px;
          content: "";
          width: 20px;
          height: 20px;
          background-image: url(../../assets/img/common/icon--arrow-up-black.svg);
          transform: scaleY(-1);
          transition: transform 0.3s ease, opacity 0.2s ease;
          opacity: 0.3;

          &.disabled {
            opacity: 0.1 !important;
          }
        }

        .next {
          position: absolute;
          top: 20px;
          right: 20px;
          content: "";
          height: 20px;
          font-size: 14px;
          font-weight: 500;
          line-height: 20px;
          color: #ec0047;
          transition: opacity 0.2s ease;
          opacity: 0;

          &[disabled] {
            cursor: not-allowed;
            color: #444;
          }
        }

        .bullet {
          display: inline-block;
          vertical-align: middle;
          position: relative;
          width: 26px;
          height: 26px;
          border: solid 1px #bbb;
          background-color: #fff;
          border-radius: 20px;
          transition: background-color 0.2s ease,
                      border-color 0.2s ease;

          i {
            display: block;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            transition: opacity 0.2s ease;

            &.icon {
              opacity: 0;
            }

            &.number {
              font-size: 14px;
              font-weight: 500;
              color: #888;
              transition: color 0.2s ease;
            }
          }
        }

        h4 {
          margin: 0 10px;
          display: inline-block;
          vertical-align: middle;
          font-size: 18px;
          font-weight: 500;
        }
      }

      .step-body {
        margin-top: -13px;
        opacity: 0.3;
        transition: opacity 0.2s ease;

        .step-left {
          display: inline-block;
          vertical-align: top;
          width: 485px;
        }

        .step-right {
          display: inline-block;
          vertical-align: top;
          width: calc(100% - 485px);

          .ui_radio /deep/ .icon{
            background-color: #ffffff;
            border-radius: 100%;
          }
          .preview-222222 {
           .ui_radio /deep/ .icon{
            background-color: transparent;
            }
           .ui_radio.on /deep/ .icon{
            background-color: #ffffff;

             background-image: url(../../assets/img/research/icon--radio-ffffff.svg);
            }
          }
        }

        .step-item {
          margin: 19px 30px;
          border-top: dashed 1px #d7d7d7;
          padding-bottom: 20px;

          &:first-child {
            border-top: none;
          }

          .ui_radio /deep/ .two-line {
            display: inline-block;
            position: absolute;
            line-height: 1.36;
            top: -3px;
            left: 25px;

            .tooltip {
              position: absolute;
              top: 2.5px;
              right: -5px;
            }
          }

          h5 {
            font-size: 16px;
            font-weight: 500;
            color: #444;
            padding: 18px 0 9px 0;
            white-space: nowrap;

            .icon--required {
              vertical-align: top;
              margin-right: 5px;
            }

            .description {
              font-size: 12px;
              font-weight: normal;
              color: #289eef;
              margin-left: 5px;

              &.ja {
                letter-spacing: -1px;
              }
            }

            & /deep/ .tooltip {
              font-weight: normal;
            }
          }

          & /deep/ textarea {
            margin-top: -3px;
          }

          & /deep/ .bubble .text {
            margin-left: 0;
          }

          &.privacy {
            border-top: inherit;
            margin-top: 10px;

            .input--wrap {
              margin-top: 0px;
            }

            .title {
              .label {
                font-size: 14px;
                font-weight: 500;
                font-stretch: normal;
                font-style: normal;
                line-height: 1;
                letter-spacing: normal;
                color: #2f2f2f;

                margin-left: 10px;
                line-height: 22px;
                vertical-align: middle;
              }

              & /deep/ .ba-switch {
                button {
                  margin-top: 0px;
                }
              }
            }

            .desc {
              margin-top: 8px;
              line-height: 12px;
              height: 12px;

              .txt {
                color: #289eef;
                font-size: 12px;
                font-weight: normal;
                font-stretch: normal;
                font-style: normal;
                letter-spacing: normal;
                display: inline-block;
              }

              .example {
                float: right;
                display: inline-block;
                height: 12px;
                line-height: 12px;

                span {
                  display: inline-block;
                  font-size: 12px;
                  font-weight: 500;
                  font-stretch: normal;
                  font-style: normal;
                  line-height: 1;
                  color: #767676;
                  margin-right: 2px;
                }

                .icon {
                  position: relative;
                  top:1px;
                }
              }
            }

            .content {
              margin-top: 15px;
            }
          }

          &.questions {
            padding-top: 22px;

            h5 {
              padding-left: 5px;
              padding-bottom: 0;
            }

            .question-items {
              .question {
                margin: 20px 0;
                border-radius: 13px;
                box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25), 0 6px 6px -4px rgba(0, 0, 0, 0.16);
                background-color: #fff;

                &:first-child {
                  margin-top: 15px;
                }

                &.ghost {
                  background-color: #f4f4f4;
                  box-shadow: none;

                  * {
                    opacity: 0;
                  }
                }

                .handle {
                  cursor: move;
                  padding: 12px 0 2px;

                  &::before,
                  &::after {
                    content: "";
                    display: block;
                    border-radius: 1px;
                    height: 2px;
                    background-color: #bbb;
                    margin: 3px auto 0;
                    width: 28px;
                  }
                }

                .buttons {
                  position: relative;
                  text-align: right;
                  padding: 0 24px;

                  button {
                    vertical-align: top;
                    transition: opacity 0.2s ease;
                    overflow: visible;

                    &.required {
                      font-size: 12px;
                      font-weight: 500;
                      color: #767676;
                      margin-left: 22px;

                      .ui_checkbox {
                        margin-left: 7px;
                      }
                    }

                    &[disabled] {
                      opacity: 0.3;
                    }
                  }

                  .gap {
                    display: inline-block;
                    vertical-align: middle;
                    width: 1px;
                    height: 12px;
                    margin: 4px 10px;
                    background-color: #d7d7d7;
                  }
                }

                .head {
                  margin-top: -15px;
                  padding: 0 25px;

                  .index {
                    font-size: 22px;
                    font-weight: 500;
                    color: #555;
                  }

                  .type {
                    position: relative;
                    display: inline-block;
                    width: 220px;
                    vertical-align: top;

                    .bubble {
                      z-index: 100;
                      position: absolute;
                      bottom: 50px;
                      left: 50%;
                      transform: translate(-50%, 20px);
                      opacity: 0;
                      pointer-events: none;
                      transition: transform 0.15s ease, opacity 0.15s ease;
                    }

                    &:hover {
                      .bubble {
                        transform: translate(-50%, 0);
                        opacity: 1;
                      }
                    }
                  }

                  .title {
                    display: inline-block;
                    width: calc(100% - 225px);
                    margin-left: 5px;
                    vertical-align: top;
                  }

                  .input--wrap {
                    margin-top: 0;
                  }

                  .type-tooltip {
                    z-index: 100;
                    position: absolute;
                    left: 225px;
                    width: 280px;
                    padding: 25px;
                    border-radius: 6px;
                    background-color: #2f2f2f;
                    transition: opacity 0.2s ease;

                    .type-name {
                      font-size: 15px;
                      font-weight: 500;
                      color: #ffd100;
                    }

                    .type-description {
                      margin: 20px 0 15px;
                      font-size: 13px;
                      line-height: 1.62;
                      color: #ebebeb;
                    }

                    .type-example {
                      border-top: 1px solid #555;
                      padding-top: 20px;

                      .exp-title {
                        font-size: 14px;
                        font-weight: 500;
                        color: #fff;
                        margin-bottom: 15px;
                      }

                      p {
                        font-size: 13px;
                        line-height: 1.62;
                        color: #aaa;
                        margin: 5px 0 15px;
                      }

                      ul {
                        &:last-child {
                          margin-bottom: -5px;
                        }

                        & + .exp-title {
                          margin-top: 30px;
                        }

                        li {
                          font-size: 13px;
                          line-height: 1.62;
                          color: #aaa;
                          margin-bottom: 5px;

                          span.radio {
                            display: inline-block;
                            vertical-align: middle;
                            border: 2px solid #aaa;
                            width: 14px;
                            height: 14px;
                            border-radius: 8px;
                            margin-right: 3px;
                          }

                          span.check {
                            display: inline-block;
                            vertical-align: middle;
                            border: 2px solid #aaa;
                            width: 12px;
                            height: 12px;
                            border-radius: 2px;
                            margin: 3px 5px 3px 0;
                          }

                          span.input-text {
                            display: inline-block;
                            vertical-align: middle;
                            width: 207px;
                            height: 27px;
                            margin: -1px 0 -1px 5px;
                            border-radius: 4px;
                            border: solid 1px #bbb;
                            background-color: #505050;
                          }
                        }
                      }

                      .input-textarea {
                        height: 136px;
                        margin: 15px 0 0;
                        border-radius: 4px;
                        border: solid 1px #bbb;
                        background-color: #505050;

                        span {
                          display: inline-block;
                          width: 1px;
                          height: 16px;
                          background-color: #bbb;
                          margin: 12px 11px;
                        }
                      }
                    }

                    .input-range {
                      ul {
                        white-space: nowrap;

                        li {
                          position: relative;
                          font-size: 14px;
                          line-height: 1.36;
                          color: #aaa;
                          display: inline-block;
                          vertical-align: bottom;
                          width: 65px;

                          em {
                            display: block;
                            width: 18px;
                            text-align: center;
                            margin-bottom: 7px;
                          }

                          span {
                            display: block;
                            vertical-align: middle;
                            background: #fff;
                            border: 2px solid #aaa;
                            width: 14px;
                            height: 14px;
                            border-radius: 8px;
                          }

                          &::after {
                            position: absolute;
                            bottom: 8px;
                            left: 18px;
                            display: block;
                            content: "";
                            width: 40px;
                            border-bottom: 2px dashed #aaa;
                          }

                          &:last-child {
                            &::after {
                              display: none;
                            }
                          }
                        }
                      }

                      .range-text {
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 1.36;
                        color: #aaa;
                        margin: 8px 0;

                        .left {
                          float: left;
                        }

                        .right {
                          float: right;
                        }
                      }
                    }

                    &.multiple {
                      top: 12px;
                    }

                    &.essay {
                      top: 51px;

                      .type-example {
                        padding-top: 15px;
                      }
                    }

                    &.preference {
                      top: 90px;

                      .type-example {
                        padding-top: 14px;
                      }
                    }

                    &.fade-enter-active,
                    &.fade-leave-active {
                      opacity: 1;
                    }
                    &.fade-enter,
                    &.fade-leave-to{
                      opacity: 0;
                    }
                  }
                }

                .options {
                  padding: 25px;

                  textarea {
                    width: calc(100% - 26px);
                    height: calc(120px - 18px);
                    margin: 5px 0 4px;
                    padding: 8px 12px;
                    border-radius: 6px;
                    border: solid 1px #d7d7d7;
                    background-color: #e6e6e6;
                    font-size: 14px;
                    line-height: 1.57;
                    color: #999;
                    resize: none;
                  }

                  .multiple {
                    position: relative;
                    margin: 5px 10px;
                    font-size: 14px;
                    line-height: 1.36;
                    color: #444;

                    .ui_checkbox {
                      margin-left: 10px;
                    }

                    .bubble {
                      z-index: 100;
                      position: absolute;
                      bottom: 30px;
                      left: 0;
                      transform: translate(0, 20px);
                      opacity: 0;
                      pointer-events: none;
                      transition: transform 0.15s ease, opacity 0.15s ease;
                    }

                    &:hover {
                      .bubble {
                        transform: translate(0, 0);
                        opacity: 1;
                      }
                    }
                  }

                  .option {
                    margin-top: -15px;

                    li {
                      margin-top: 18px;

                      &.other {
                        margin-top: 14px;
                        font-size: 14px;
                        line-height: 1.36;
                        color: #444;

                        .other-text {
                          display: inline-block;
                          vertical-align: middle;
                          width: 48px;
                          margin-top: 16px;
                        }

                        .input--wrap {
                          width: 737px;
                        }

                        button {
                          top: 8px;
                        }
                      }
                    }

                    .ui_checkbox,
                    .ui_radio {
                      margin-left: 10px;
                      margin-right: 2px;
                      margin-top: 14px;
                      display: inline-block;
                      vertical-align: middle;
                    }

                    .input--wrap {
                      width: 784px;
                      margin-top: 0;
                      display: inline-block;
                      vertical-align: middle;
                    }

                    button {
                      position: relative;
                      top: 4px;
                      width: 32px;
                      height: 32px;
                      margin-left: 5px;
                      padding: 8px;
                      transition: background-color 0.15s ease,
                                  opacity 0.15s ease;
                      border-radius: 20px;
                      display: inline-block;
                      vertical-align: middle;

                      &:hover:not([disabled]) {
                        background-color: rgba(0, 0, 0, 0.06);
                      }

                      &[disabled] {
                        opacity: 0.3;
                      }
                    }
                  }

                  .add-option {
                    text-align: right;
                    padding: 28px 0 0;

                    button {
                      position: relative;
                      min-width: 85px;
                      height: 34px;
                      padding: 9px 13px 9px 30px;
                      margin-left: 5px;
                      border-radius: 20px;
                      border: solid 1px #bbb;
                      background-color: #fff;
                      vertical-align: middle;
                      font-size: 13px;
                      line-height: 1;
                      color: #444;
                      transition: background-color 0.15s ease;

                      .icon {
                        position: absolute;
                        top: 50%;
                        left: 10px;
                        transform: translateY(-50%);
                      }

                      &:hover:not([disabled]) {
                        background-color: #f0f0f0;
                      }

                      &[disabled] {
                        opacity: 0.3;
                      }
                    }
                  }

                  .preference {
                    position: relative;
                    display: inline-block;
                    vertical-align: middle;
                    margin: -5px 0 9px;

                    .bubble {
                      z-index: 100;
                      position: absolute;
                      bottom: 50px;
                      left: 50%;
                      transform: translate(-50%, 20px);
                      opacity: 0;
                      pointer-events: none;
                      transition: transform 0.15s ease, opacity 0.15s ease;
                    }

                    &:hover {
                      .bubble {
                        transform: translate(-50%, 0);
                        opacity: 1;
                      }
                    }

                    &.description {
                      width: 327px;

                      &.left {
                        margin-right: 20px;
                      }

                      &.right {
                        margin-left: 20px;
                      }
                    }

                    &.number {
                      width: 68px;
                    }

                    &.wave {
                      width: 10px;
                      text-align: center;
                      margin: -2px 10px 0;
                      font-size: 14px;
                      line-height: 1.57;
                      color: #2f2f2f;
                    }

                    .input--wrap {
                      margin-top: 0;
                    }
                  }
                }
              }
            }

            .add-question {
              display: flex;
              justify-content: center;
              text-align: center;
              padding: 0 0 1px;

              button {
                display: flex;
                align-items: center;
                height: 38px;
                line-height: 38px;
                padding: 0 15px 0 12px;
                border-radius: 20px;
                border: solid 1px #bbb;
                background-color: #fff;
                transition: background-color 0.15s ease, opacity 0.2s ease;

                &:hover:not([disabled]) {
                  background-color: #f0f0f0;
                }

                &:disabled {
                  opacity: 0.3;
                }

                .icon {
                  vertical-align: middle;
                  margin-top: -1px;
                }

                span {
                  display: inline-block;
                  vertical-align: middle;
                  font-weight: 500;
                  color: #666;
                  font-size: 14px;
                  padding: 0 5px 0 15px;
                }
              }
            }
          }

          .item-description {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #e60724;
            padding-top: 8px;

            &.with-icon {
              padding-top: 0;
              margin-top: -3px;
            }
          }

          .item-warning {
            display: block;
            font-size: 12px;
            font-weight: 500;
            color: #555555;
            padding: 6px 0 10px;

            i {
              vertical-align: middle;
              margin: -1px 5px 1px 0;
            }
          }

          .item-fixed {
            font-size: 14px;
            line-height: 1.36;
            color: #444;
            margin: 19px 20px;
          }

          .item-checkbox-option {
            padding: 0 20px 4px;

            &.popup {
              padding: 30px 0;
            }

            &.language {
              padding-top: 23px;
              padding-bottom: 1px;
            }

            &.color {
              padding: 19px 80px 0 16px;

              .ui_radio {
                display: inline-block;
                vertical-align: top;
                padding-top: 40px;
                margin-right: 26px;
                margin-bottom: 35px;
                width: 30px;

                & /deep/ {
                  .icon {
                    top: 40px;
                    left: calc(50% - 9px);
                    transform: translate(0);
                  }

                  .color-chip {
                    position: absolute;
                    top: 0;
                    left: 0;
                    display: block;
                    width: 28px;
                    height: 28px;
                    border-radius: 4px;
                    border: solid 1px #999;

                    .color-pane {
                      display: block;
                      width: 100%;
                      height: 100%;
                      background-color: #fff;
                      border-radius: 3px;
                    }

                    &.color-ffffff {
                      border-color: #999 !important;
                    }

                    &.color-222222 {
                      border-color: #222 !important;
                      background-color: #222 !important;

                      .color-pane {
                        background-color: #222 !important;
                      }
                    }

                    &.color-d32054 {
                      .color-pane {
                        background-color: #FBECEF !important;
                      }
                    }

                    &.color-c76000 {
                      .color-pane {
                        background-color: #F9EDE2 !important;
                      }
                    }

                    &.color-735c0f {
                      .color-pane {
                        background-color: #F3F1D0 !important;
                      }
                    }

                    &.color-458200 {
                      .color-pane {
                        background-color: #EAF5D0 !important;
                      }
                    }

                    &.color-008082{
                      .color-pane {
                        background-color: #DAF5F5 !important;
                      }
                    }

                    &.color-005dc6 {
                      .color-pane {
                        background-color: #DFEFFF !important;
                      }
                    }

                    &.color-5459e5 {
                      .color-pane {
                        background-color: #EBECFF !important;
                      }
                    }

                    &.color-7f1d57 {
                      .color-pane {
                        background-color: #FBEDFA !important;
                      }
                    }
                  }
                }
              }
            }

            .radio-popup {
              display: inline-block;
              vertical-align: top;
              width: 190px;
              height: 181px;
              padding-left: 20px;
              white-space: nowrap;

              .ui_select {
                margin: 7px 30px 7px 25px;
              }

              & /deep/ {
                .tooltip {
                  margin-top: 7px;
                  margin-left: 2px;
                }

                .image {
                  width: 170px;
                  height: 115px;
                  margin-top: 29px;

                  &.popover {
                    background-image: url(../../assets/img/research/asset--popover.svg);
                  }

                  &.fullscreen {
                    background-image: url(../../assets/img/research/asset--fullscreen.svg);
                  }
                }
              }
            }

            .radio-language{
              height: 38px;
              white-space: nowrap;
              display: flex;
              align-items: center;

              .ui_select {
                margin: 0 0 0 30px;
              }
              .ui_radio {
                margin-right: 0;
              }

              & /deep/ {
                .tooltip {
                  margin-top: 7px;
                  margin-left: 5px;
                }
              }
            }

            .ui_radio,
            .ui_checkbox {
              margin-right: 47px;
              margin-left: 0;
            }

            .date--wrap {
              width: 150px;
              margin: -50px 0;
              vertical-align: middle;

              & /deep/ label {
                display: none;
              }

              &.fade-enter-active, &.fade-leave-active {
                opacity: 1;
                transition: opacity 0.2s ease, width 0.2s cubic-bezier(.28,.12,.42,.94);
              }

              &.fade-enter, &.fade-leave-to{
                opacity: 0;
                width: 0;
              }
            }

            .ui_select {
              &.fade-enter-active, &.fade-leave-active {
                opacity: 1;
                transition: opacity 0.2s ease;
              }

              &.fade-enter, &.fade-leave-to{
                opacity: 0;
              }
            }
          }

          .item-line-option {
            padding: 0 21px 4px;
            margin-top: -22px;
            font-size: 14px;
            line-height: 1.57;
            color: #555;

            .left {
              margin-right: 20px;
            }

            .right {
              margin-left: 20px;
            }

            .ui_select {
              margin-right: 10px;
            }

            .item-item {
              margin: 48px 0;

              h6 {
                font-size: 14px;
                font-weight: 500;
                color: #2f2f2f;

                &.utm {
                  height: 41px;
                }

                .tooltip {
                  margin: 2px 0 0 5px;
                }
              }

              .option-url {
                margin: 20px 0 1px;

                & /deep/ .label {
                  margin-bottom: 0;
                }

                .left {
                  margin-right: 20px;
                }

                .right {
                  margin-left: 20px;
                }

                .input--wrap {
                  margin-top: 0;
                }

                .url-match {
                  display: inline-block;
                  vertical-align: top;
                  width: 245px;
                  margin-right: 10px;
                }

                .url-analyze {
                  display: inline-block;
                  width: calc(100% - 255px);
                  vertical-align: top;
                  transition: width 0.3s cubic-bezier(0, 0, 0, 1);
                  overflow: visible;

                  &.short {
                    width: calc(100% - 255px - 195px);
                  }
                }

                .url-regexp {
                  display: inline-block;
                  width: 195px;
                  text-align: right;
                  overflow: hidden;
                  transition: width 0.3s cubic-bezier(0, 0, 0, 1),
                              opacity 0.3s ease;

                  .input--wrap {
                    width: 185px;
                    margin-left: 10px;
                  }

                  &.hidden {
                    width: 0;
                    opacity: 0;
                  }
                }
              }

              .utm-options {
                margin-top: -12px;

                li {
                  margin-bottom: 8px;
                }

                .checkbox {
                  display: inline-block;
                  vertical-align: middle;
                  padding-top: 20px;

                  .ui_checkbox {
                    margin-left: -2px;
                  }
                }

                .label {
                  display: inline-block;
                  vertical-align: middle;
                  width: 74px;
                  font-size: 13px;
                  font-weight: 500;
                  color: #767676;
                  text-align: right;
                  padding: 20px 10px 0 0;
                }

                .value {
                  display: inline-block;
                  vertical-align: middle;
                  width: 541px;
                  margin-right: 5px;

                  .input--wrap {
                    margin-top: 0;

                    & /deep/ .validation-text {
                      font-size: 12px;
                      font-weight: 500;
                      margin-top: -3px;
                      color: #e60724;
                    }
                  }
                }

                .condition {
                  display: inline-block;
                  vertical-align: middle;
                  width: 215px;
                }
              }

              &.match {
                opacity: 0;
                max-height: 0;
                margin: 48px 0 0 0;
                transition: opacity 0.3s ease,
                            max-height 0.3s cubic-bezier(.28,.12,.42,.94),
                            margin 0.3s cubic-bezier(.28,.12,.42,.94);

                &.show {
                  max-height: 174px;
                  margin: 48px 0;
                  opacity: 1;
                }

                .tooltip /deep/ .text {
                  text-align: left;
                  font-weight: normal;

                  strong {
                    font-weight: bold;
                  }
                }
              }

              &.utm {
                opacity: 0;
                height: 0;
                margin: -48px 0 0 0;
                transition: opacity 0.3s ease,
                            height 0.3s cubic-bezier(.28,.12,.42,.94),
                            margin 0.3s cubic-bezier(.28,.12,.42,.94);

                &.show {
                  height: 338px;
                  margin: 48px 0;
                  opacity: 1;
                }
              }
            }
          }

          .item-validation {
            padding: 22px 20px 30px;
            border-radius: 5px;
            border: solid 1px #d7d7d7;
            background-color: #f4f4f4;
            margin-top: 40px;

            .vali-title {
              h6 {
                display: inline-block;
                vertical-align: top;
                width: 197px;
                font-size: 14px;
                font-weight: 500;
                padding-top: 3px;
                color: #2f2f2f;
              }

              p {
                display: inline-block;
                vertical-align: top;
                width: calc(100% - 197px);
                font-size: 13px;
                line-height: 1.62;
                color: #666;
              }
            }

            .vali-value {
              margin-top: -10px;

              .input--wrap {
                width: calc(100% - 104px);
                margin-right: 5px;
                vertical-align: bottom;
              }

              .ui-btn {
                width: 99px;
                vertical-align: bottom;
              }
            }
          }
        }

        .item-preview {
          position: relative;
          width: 375px;
          height: 631px;
          margin: 55px 0 0 48px;
          border-radius: 8px;
          border: solid 1px #c5c5c5;
          background-image: linear-gradient(180deg, #DDDDDD 0%, #F1F1F1 57.59%);
          overflow: hidden;

          &.preview-popup-style.preview-right.desktop{
            &::before{
              content: '';
              position: absolute;
              left: 0;
              height: 100%;
              bottom: 0;
              width: 73px;
              background: linear-gradient(90deg, rgba(241, 241, 241, 0.0001) 0%, #F1F1F1 100%);
              transform: matrix(-1, 0, 0, 1, 0, 0);
              z-index: 1;
            }
          }
          &.preview-popup-style.preview-left.desktop{
            &::before{
              content: '';
              position: absolute;
              right: 0;
              height: 100%;
              bottom: 0;
              width: 73px;
              background: linear-gradient(-90deg, rgba(241, 241, 241, 0.0001) 0%, #F1F1F1 100%);
              transform: matrix(-1, 0, 0, 1, 0, 0);
              z-index: 1;
            }
          }

          .preview-header {
            position: absolute;
            top: 20px;
            left: 0;
            width: 100%;
            text-align: center;
            transition: top 0.3s ease;

            .ba-button {
              margin: 0 2px 0 3px;

              & /deep/ {
                .icon {
                  width: 20px;
                  height: 20px;
                  top: 6px;
                  left: 6px;
                }

                button {
                  width: 32px;
                }
              }
            }

            &.bottom {
              top: 579px;
            }
          }

          .preview-body {
            * {
              transition: all 0.3s ease;
            }

            .icon--close-size16 {
              background-color: transparent !important;
            }

            .preview-popover {
              position: absolute;
              bottom: 0;
              left: 0;

              .pr-header {
                height: 36px;
                overflow: hidden;
                padding: 10px 10px 0 10px;
                margin: -10px -10px 0 -10px;

                button {
                  border-radius: 12px 12px 0 0;
                  font-size: 16px;
                  line-height: 1;
                  color: #2f2f2f;
                  background: #ffffff;
                  box-shadow:  0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                  white-space: nowrap;
                  border: none;
                  padding: 9px 12px 11px 14px;
                  display: inline-flex;
                  column-gap: 30px;
                  box-sizing: border-box;
                  align-items: flex-start;
                  span{
                    font-size: 14px;line-height: 14px;font-weight: normal;columns: #666;
                  }

                  .icon {
                    vertical-align: bottom;
                    transform: scaleY(1);
                    width: 16px;
                    height: 16px;
                    background-image: url(../../assets/img/common/icon--arrow-up-black-thin.svg);
                  }
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                background-color: #fff;

                .pr-question {
                  padding: 26px 25px 22px;
                  font-size: 15px;
                  font-weight: 400;
                  line-height: 23px;
                  text-align: center;
                  color: #444444;
                  span.pr-question-pagging{
                    font-size: 12px;line-height: 23px; color: #767676;padding-left: 4px;
                  }
                }

                .pr-option {
                  background-color: #f4f4f4;
                  padding: 10px 0;

                  .ui_radio {
                    display: block;
                    margin: 0 25px;
                    padding:10px 0;
                    line-height: 21px;
                    .text{
                      color:#444444;
                    }
                  }
                }

                .pr-button {
                  font-size: 14px;
                  line-height: 1;
                  position: relative;
                  text-align: center;

                  button {
                    font-size: 15px;
                    line-height: 1;
                    color: #333;
                    i.icon--arrow-up-black{width: 16px;height:16px;}
                    &.pr-button-next{
                      padding:19px 0;
                      display: block;
                      width: 100%;
                      font-weight: 600;

                      i.icon--arrow-up-black{transform: rotate(-90deg);vertical-align:bottom;}
                    }
                    &.pr-button-prev{
                      position: absolute;
                      left:0;
                      top:0;
                      width: 16px;height: 16px;
                      padding:19px 22px;
                      display: inline-block;
                      box-sizing: content-box;
                      z-index: 1;
                      i{transform: rotate(90deg);}
                    }
                  }
                }
              }

              &.desktop {
                width: 420px;
              }

              &.phone {
                width: calc(100% - 55px);
                left: 50%; transform: translateX(-50%);

                .pr-header {
                  margin: 0;padding:0;
                  button {
                    width: 38px;
                    margin-top: 8px;
                    padding: 7px 11px 5px 11px;
                    border-radius: 8px 8px 0 0;
                    .preview-hide-bg-radius{display: none;}
                    span {
                      display: none;
                    }

                    .icon {
                      margin-left: 0;
                    }
                  }
                }

                .pr-body {
                  border-radius: 8px 8px 0 0;
                  .pr-question{
                    padding:22px 20px;
                    font-weight: 400;
                    .pr-question-pagging{font-weight: 400;}
                  }

                  .pr-button {
                    overflow: hidden;
                    button.pr-button-prev{
                      padding:19px 20px;
                    }
                  }
                }
              }

              &.hidden.desktop {
                transform: translateY(calc(100% - 39px));

                .pr-header {
                  button {
                    .icon {
                      transform: scaleY(-1);
                    }
                  }
                }
              }
              &.hidden.phone{
                transform: translate(-50%, calc(100% - 38px));
              }

              &.preview-left {
                &.desktop {
                  left: 20px;
                  .pr-header {
                    button {
                      position: relative;
                      &::before{
                        content: "";
                        width: 6px;
                        height: 6px;
                        border: 2px solid #fff;
                        position: absolute;
                        border-left: none;
                        border-bottom: none;
                        border-radius: 0 6px 0 0;
                        right: -6px;
                        bottom: -2px;
                        transform: rotate(180deg);
                      }
                      .preview-hide-bg-radius{
                        display: none;
                        position: absolute;
                        background: #fff;
                        height: 30px;
                        width: 10px;
                        border-radius: 12px;
                        border-bottom-left-radius: 0px;
                        border-top-left-radius: 0;
                        right: 0px;
                        bottom: 0;
                        transition: all 0.3s ease;
                        &::before{
                          content: '';
                          position: absolute;
                          z-index: 2;
                          height: 10px;
                          width: 10px;
                          left: 100%;
                          bottom: 0;
                          background: #fff;
                          -webkit-mask: radial-gradient( farthest-side at top right, transparent 99%, #fff 100% );
                          mask: radial-gradient( farthest-side at top left, transparent 99%, #fff 100% );
                          transition: all 0.3s ease;
                        }
                        &::after{
                          content: '';
                          position: absolute;
                          left: 100%;
                          bottom: 0px;
                          z-index: 3;
                          height: 23px;
                          width: 10px;
                          background-color: transparent;
                          border-bottom-left-radius: 20px;
                          border-bottom: 1px solid rgba(0,0,0,0.1);
                          border-left: 1px solid rgba(0,0,0,0.05);
                          box-shadow: inset -0.5px -1px -0.15px rgba(0, 0, 0, 0.15);
                        }
                      }
                    }
                  }

                  .pr-body {
                    border-radius: 0 12px 0 0;
                  }
                }

                &.phone {
                 .pr-header {
                   button {
                     margin-left: 20px;
                   }
                 }
                }
              }

              &.preview-right {
                .pr-header {
                  padding-left: 100%;
                }

                &.desktop {
                  left: calc(-100% + 310px);

                  .pr-header {
                    button {
                      transform: translateX(-100%);
                      margin-left: 10px;
                      &::before{
                        content: "";
                        width: 6px;
                        height: 6px;
                        border: 2px solid #fff;
                        position: absolute;
                        border-left: none;
                        border-bottom: none;
                        border-radius: 0 6px 0 0;
                        left: -6px;
                        bottom: -2px;
                        transform: rotate(90deg);
                      }
                      .preview-hide-bg-radius{
                        display: none;
                        position: absolute;
                        background: #fff;
                        height: 30px;
                        width: 10px;
                        border-radius: 12px;
                        border-bottom-right-radius: 0px;
                        border-top-right-radius: 0;
                        left: 0px;
                        bottom: 0;
                        transition: all 0.3s ease;
                        &::before{
                          content: '';
                          position: absolute;
                          z-index: 2;
                          height: 10px;
                          width: 10px;
                          right: 100%;
                          bottom: 0;
                          background: #fff;
                          -webkit-mask: radial-gradient( farthest-side at top left, transparent 99%, #fff 100% );
                          mask: radial-gradient( farthest-side at top left, transparent 99%, #fff 100% );
                          transition: all 0.3s ease;
                        }
                        &::after{
                          content: '';
                          position: absolute;
                          right: 100%;
                          bottom: 0px;
                          z-index: 3;
                          height: 23px;
                          width: 10px;
                          background-color: transparent;
                          border-bottom-right-radius: 20px;
                          border-bottom: 1px solid rgba(0,0,0,0.1);
                          border-right: 1px solid rgba(0,0,0,0.05);
                          box-shadow: inset -0.5px -1px -0.15px rgba(0, 0, 0, 0.15);
                        }
                      }
                    }
                  }

                  .pr-body {
                    border-radius: 12px 0 0 0;
                  }
                }

                &.phone {
                  .pr-header {
                    button {
                      transform: translateX(calc(-100% - 20px));
                    }
                  }
                }
              }
            }

            .preview-fullscreen {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              border-radius: 14px;
              box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              background-color: #fff;

              .pr-header {
                position: absolute;
                top: 12px;
                right: 12px;

                button {
                  box-shadow: none !important;
                }
              }

              .pr-body {
                border-radius: 12px;
                box-shadow: none !important;

                .pr-question {
                  padding: 40px 25px 22px;
                  font-size: 15px;
                  font-weight: 400;
                  line-height: 23px;
                  text-align: center;
                  color: #444444;
                  span.pr-question-pagging{font-size: 12px;line-height:23px;display:inline-block;margin-left: 4px;color:#767676;}
                }

                .pr-option {
                  background-color: #f4f4f4;
                  padding: 10px 0;

                  .ui_radio {
                    display: block;
                    margin: 0 25px;
                    padding:10px 0;
                    font-size: 14px;line-height: 21px;font-weight: 400;color:#444;
                  }
                }

                .pr-button {
                  font-size: 14px;
                  line-height: 1;
                  position: relative;
                  text-align: center;

                  button {
                    font-size: 15px;
                    line-height: 1;
                    color: #333;
                    i.icon--arrow-up-black{width: 16px;height:16px;}
                    &.pr-button-next{
                      padding:19px 0;
                      display: block;
                      width: 100%;
                      font-weight: 600;

                      i.icon--arrow-up-black{transform: rotate(-90deg);vertical-align:bottom;}
                    }
                    &.pr-button-prev{
                      position: absolute;
                      left:0;
                      top:0;
                      width: 16px;height: 16px;
                      padding:19px 22px;
                      display: inline-block;
                      box-sizing: content-box;
                      z-index: 1;
                      i{transform: rotate(90deg);}
                    }
                    &.pr-button-skip{
                      position: absolute;
                      right: 0;
                      top:0;
                      padding:15px 25px 15px 0;
                      display: inline-block;
                      box-sizing: content-box;
                      z-index: 1;
                      span{font-size: 12px;line-height: 16px;padding:4px 8px;display: inline-block;color:#888;border: 1px solid #D7D7D7;border-radius: 4px;}
                    }
                  }
                }
              }

              &.desktop {
                width: calc(100% - 40px);
              }

              &.phone {
                top: 0;
                transform: translate(-50%, 0);
                width: calc(100% + 0.5px);
                border-radius: 0;

                .pr-body {
                  border-radius: 0;

                  .pr-question {
                    padding: 37px 20px 20ㅔㅌ;
                  }
                }

                .pr-button {

                }
              }
            }

            &.preview-ffffff {
              & .pr-body {
                & .pr-option {
                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-ffffff.svg);
                    }
                  }
                }
              }
            }

            &.preview-222222 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
                .pr-header {
                  button {
                    i.icon{
                      background-image: url(../../assets/img/icon_close_size16_white.svg);
                      background-color: #fff;
                      transform: none;
                    }
                  }
                }

                .pr-body {
                  .pr-button{
                    background-color: transparent;
                    button{
                      background-color: transparent;
                      &.pr-button-skip span{
                        border-color:#444444;
                      }
                    }
                  }
                }

              }

              .pr-header {
                button {
                  color: #ffffff;
                  background-color: #202020;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                  .preview-hide-bg-radius{
                    display: none;
                    background-color: #202020 !important;
                    &::before{
                      background-color: #202020 !important;
                      }
                  }
                  i.icon{
                     background-image: url(../../assets/img/common/icon--arrow-up-white-thin.svg);
                  }
                }
              }

              .pr-body {
                background-color: #202020;
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-question {
                  color: #cccccc;
                }

                .pr-option {
                  background-color: #333333;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #b7b7b7;
                    vertical-align: middle;
                  }
                }

                .pr-button {
                  color: rgba(255, 255, 255, 0.4);

                  button {
                    background-color: #202020;
                    color: #ffffff;
                    i{
                      background-image: url(../../assets/img/common/icon--arrow-medium-right-white.svg);
                    }
                    &.pr-button-prev i.icon--arrow-up-black{
                      transform:none;
                      background-image: url(../../assets/img/common/icon--arrow-left-gray.svg);
                    }
                    &.pr-button-next i.icon--arrow-up-black{
                      transform:none;
                    }
                  }
                }
              }

              .hidden.desktop{
                 .pr-header{
                   button{
                     i.icon{
                       transform: rotate(-90deg);
                     }
                   }
                 }
              }
              .preview-right.desktop{
                .pr-header button::before{
                  border-color: #202020;
                }
              }
              .preview-left.desktop{
                .pr-header button::before{
                  border-color: #202020;
                }
              }
            }

            &.preview-d32054 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #FBECEF;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #6b253a;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-d32054.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-c76000 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #F9EDE2;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #634020;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-c76000.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-735c0f {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #F3F1D0;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #735c0f;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-735c0f.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-458200 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #EAF5D0;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #3c5b19;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-458200.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-008082 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #DAF5F5;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #125252;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-008082.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-005dc6 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #DFEFFF;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #264769;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-005dc6.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-5459e5 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #EBECFF;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #262669;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-5459e5.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }

            &.preview-7f1d57 {
              .preview-fullscreen {
                box-shadow: 0px 0px 0px 1px rgba(0, 0, 0, 0.16), 0px 8px 15px rgba(0, 0, 0, 0.16);
              }

              .pr-header {
                button {
                  color: #666666;
                  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);
                }
              }

              .pr-body {
                box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0px 2px rgba(0, 0, 0, 0.2);

                .pr-option {
                  background-color: #FBEDFA;
                  border:none;

                  .ui_radio /deep/ .text {
                    color: #64244a;
                    vertical-align: middle;
                  }

                  .ui_radio.on /deep/ {
                    & .icon {
                      background-image: url(../../assets/img/research/icon--radio-7f1d57.svg);
                    }
                  }
                }

                .pr-button {
                  button {
                    /* background-color: #ffffff; */
                  }
                }
              }
            }
          }
        }
      }

      .step-footer {
        padding: 20px 0 25px;
        text-align: center;

        .ui-btn {
          min-width: 100px;
        }
      }

      &.step-1 {
        transition: max-height 0.5s ease;
      }

      &.step-2 {
        .step-body {
          .step-item {
            h5 {
              padding: 38px 0 9px 5px;
              height: 49px;
            }
          }
        }
      }

      &.step-3 {
        .step-body {
          .step-item {
            padding-top: 20px;
          }
        }
      }

      &.step-4 {
        .step-body {
          padding-bottom: 40px;

          .step-item {
            padding-top: 20px;
            padding-bottom: 5px;

            .item-checkbox-option {
              display: flex;
              align-items: center;
              margin: 20px 0;
            }

            &.timezone {
              padding-bottom: 5px;

              .item-checkbox-option {
                margin: 10px 0;
              }
            }
          }
        }
      }

      &.complete:not(.ing) {
        .step-header {
          opacity: 1;
        }

        .step-body {
          opacity: 1;
        }
      }

      &.ing {
        overflow: visible;

        &.step-1 {
          .step-header {
            padding-top: 20px;
          }
        }

        &.step-2 {
          max-height: 1394px;
        }

        &.step-3 {
          max-height: 1003px;
        }

        &.step-4 {
          max-height: 585px;
        }

        .step-header {
          opacity: 1;

          .arrow {
            transform: scaleY(1);
            opacity: 1;
          }

          .next {
            opacity: 1;

            &[disabled] {
              opacity: 0.3;
            }
          }

          .bullet {
            border-color: #ec0047;
            background-color: #ec0047;

            i.number {
              color: #fff;
            }
          }

          h4 {
            color: #ec0047;
          }
        }

        .step-body {
          opacity: 1;
        }
      }

      &.opened {
        .step-header {
          cursor: pointer;
          opacity: 1;

          .arrow {
            opacity: 1;
          }
        }
      }

      &.editing {
        .step-header {
          cursor: pointer;
          opacity: 1;

          .arrow {
            opacity: 1;
          }
        }
      }

      &:not(.ing) {
        max-height: 60px !important;
      }

      &.animating {
        overflow: hidden;
      }
    }
  }

  .add-footer {
    padding: 20px 0 150px;
    text-align: center;

    .button-container {
      position: relative;
      padding: 10px 0;

      .toast {
        position: absolute;
        top: -56px;
        left: 50%;
        transform: translateX(-50%);
        padding: 8px 13px 8px 12px;
        opacity: 0.85;
        border-radius: 6px;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16);
        background-color: #000;
        white-space: nowrap;
        font-size: 13px;
        line-height: 1;
        color: #57ab00;

        .icon {
          vertical-align: middle;
          transform: translateY(-1px);
          margin-left: 18px;
        }

        &.fade-enter-active,
        &.fade-leave-active {
          transition: opacity 0.3s ease;
        }
        &.fade-enter,
        &.fade-leave-to{
          opacity: 0;
        }
      }

      .bubble {
        position: absolute;
        bottom: 48px;
        left: 50%;
        transform: translate(-50%, 20px);
        opacity: 0;
        pointer-events: none;
        transition: transform 0.2s ease,
                    opacity 0.2s ease;

        & /deep/ .text {
          text-align: left;
        }
      }

      &:hover {
        .bubble {
          transform: translate(-50%, 0);
          opacity: 1;
        }
      }

      .ui-btn {
        margin: 0 2px 0 3px;
      }
    }

    .cant-preview {
      font-size: 13px;
      line-height: 1.46;
      color: #444;
      padding: 25px 0;

      em {
        cursor: pointer;
        font-weight: bold;
        color: #ec0047;
        text-decoration: underline;
      }
    }
  }

  .preview-modal {
    position: relative;
    width: 430px;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;

    .icon--close-size16 {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .modal-header {
      padding-top: 50px;
      text-align: center;

      h4 {
        font-size: 20px;
        font-weight: 500;
        color: #444;
        margin: 15px 0 12px;
      }

      p {
        font-size: 14px;
        line-height: 1.43;
        color: #2f2f2f;
      }
    }

    .modal-body {
      margin: 5px 30px 30px;
    }

    .modal-footer {
      text-align: center;
      padding: 10px 0 25px;
    }
  }

  .privacy-modal {
    position: relative;
    width: 430px;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;

    .icon--close-size16 {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .modal-header {
      padding-top: 50px;
      text-align: center;

      h4 {
        font-size: 20px;
        font-weight: 500;
        color: #444;
        margin: 15px 0 12px;
      }

      p {
        font-size: 14px;
        line-height: 1.43;
        color: #2f2f2f;
      }
    }

    .modal-body {
      border-top:2px solid #d7d7d7;
      margin: 13px 35px 0px;
      padding-top: 25px;

      & /deep/ h5 {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.43;
        letter-spacing: normal;
        color: #2f2f2f;
      }

      & /deep/ p {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.43;
        letter-spacing: normal;
        color: #767676;
        margin-left: 15px;
        margin-bottom: 20px;
      }
    }

    .modal-footer {
      text-align: center;
      padding: 10px 0 25px;
    }
  }

  .preview-help {
    position: relative;
    width: 530px;
    max-height: 90vh;
    overflow: auto;
    border-radius: 14px;
    box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
    background-color: #fff;

    .icon--close-size16 {
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .help-header {
      padding: 58px 40px 35px;

      h4 {
        font-size: 20px;
        font-weight: 500;
        line-height: 1.3;
        text-align: center;
        color: #ec0047;
      }
    }

    .help-body {
      margin: 0 40px;
      border-top: 2px solid #d7d7d7;
      border-bottom: 1px solid #d7d7d7;
      padding: 2px 0 20px;

      h5 {
        font-size: 16px;
        font-weight: 500;
        line-height: 1.63;
        color: #2f2f2f;
        margin: 30px 0 10px;

        & /deep/ em {
          color: #ec0047;
        }
      }

      p {
        margin: 10px 0;
        font-size: 14px;
        line-height: 1.57;
        color: #444;
      }
    }

    .help-footer {
      padding: 32px 0 40px;
      text-align: center;

      p {
        font-size: 13px;
        line-height: 1.46;
        color: #666;
      }
    }

    .email {
      margin-top: 15px;
      font-size: 13px;
      line-height: 1.46;
      color: #666;

      i {
        vertical-align: middle;
        transform: translateY(-1px);
        margin-right: 5px;
      }

      a {
        color: inherit;
        font: inherit;
        text-decoration: underline;
      }
    }
  }
}
</style>
